import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast, ToastContainer } from 'react-toastify';
import InfoModal from '../../../modals/InfoModal';
import ModalInnovation from '../../../modals/ModalInnovation';
import 'react-toastify/dist/ReactToastify.css';
import './DriversOfInnovationSurvey.css';
import dropdownOptions from '../../../data/dropdownOptions.json';
import * as XLSX from 'xlsx';
// Initialize IndexedDB
const initDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open('DriversOfInnovationDB', 1);
        request.onerror = () => reject(request.error);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains('surveyData')) {
                db.createObjectStore('surveyData', { keyPath: 'id' });
            }
        };

        request.onsuccess = () => resolve(request.result);
    });
};

// Save data to IndexedDB
const saveToIDB = async (data) => {
    console.log('saveToIDB called with data:', data);
    const db = await initDB();
    console.log('IndexedDB initialized');

    return new Promise((resolve, reject) => {
        console.log('Starting IndexedDB transaction');
        const transaction = db.transaction(['surveyData'], 'readwrite');
        const store = transaction.objectStore('surveyData');

        const request = store.put({
            id: 'currentState',
            timestamp: new Date().toISOString(),
            data: {
                ...data,
                futureMarkets: data.futureMarkets,
                asianCountries: data.asianCountries
            }
        });

        request.onerror = () => {
            console.error('IndexedDB save error:', request.error);
            reject(request.error);
        };

        request.onsuccess = () => {
            console.log('IndexedDB save successful');
            resolve(request.result);
        };
    });
};

// Load data from IndexedDB
const loadFromIDB = async () => {
    const db = await initDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(['surveyData'], 'readonly');
        const store = transaction.objectStore('surveyData');

        const request = store.get('currentState');

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result?.data || null);
    });
};

// Add this function before getToken()
async function generateRequestSignature(email, timestamp) {
    // Create a string to hash: email + timestamp + window location
    const dataToSign = `${email}:${timestamp}:${window.location.origin}`;

    // Use SubtleCrypto to create a SHA-256 hash
    const msgBuffer = new TextEncoder().encode(dataToSign);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
}

async function getToken() {
    try {
        const timestamp = Date.now();
        const emailParam = new URLSearchParams(window.location.search).get(process.env.REACT_APP_API_KEY_PARAM);

        if (!emailParam) {
            throw new Error('No email parameter found');
        }

        const signature = await generateRequestSignature(emailParam, timestamp);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get-token`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                email: emailParam,
                timestamp,
                signature
            })
        });

        if (!response.ok) {
            throw new Error(`Failed to get token: ${response.status}`);
        }

        const data = await response.json();
        return data.token;
    } catch (error) {
        throw error;
    }
}

function DriversOfInnovationSurvey() {

    const [topics, setTopics] = useState([]);
    const [buckets, setBuckets] = useState({
        topicsBucket: [],
        businessOps_p0: [], businessOps_p1: [], businessOps_p2: [], businessOps_p3: [], businessOps_p4: [], businessOps_p5: [],
        marketRegulation_p0: [], marketRegulation_p1: [], marketRegulation_p2: [], marketRegulation_p3: [], marketRegulation_p4: [], marketRegulation_p5: [],
        productMaintenance_p0: [], productMaintenance_p1: [], productMaintenance_p2: [], productMaintenance_p3: [], productMaintenance_p4: [], productMaintenance_p5: [],
        newProduct_p0: [], newProduct_p1: [], newProduct_p2: [], newProduct_p3: [], newProduct_p4: [], newProduct_p5: [],
        emerging_p0: [], emerging_p1: [], emerging_p2: [], emerging_p3: [], emerging_p4: [], emerging_p5: []
    });
    const [activeDriver, setActiveDriver] = useState(null);
    const [initialData, setInitialData] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [idbLoaded, setIdbLoaded] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [option1, setOption1] = useState('');
    const [option2, setOption2] = useState('');
    const [option3, setOption3] = useState('');
    const [showAddTopicModal, setShowAddTopicModal] = useState(false);
    const [activeAddPillar, setActiveAddPillar] = useState(null);
    const [newTopic, setNewTopic] = useState('');
    const [customTopics, setCustomTopics] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [email, setEmail] = useState('');
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [showResetDialog, setShowResetDialog] = useState(false);
    const [showTopicModal, setShowTopicModal] = useState(false);
    const [selectedTopicData, setSelectedTopicData] = useState(null);

    // New state variables for collaboration questions
    const [currentCollaborations, setCurrentCollaborations] = useState({
        researchPartnerships: false,
        thirdLevelUniversity: false,
        jointVentures: false,
        startupInvestments: false,
        other: '',
        customCollaborations: [] // Array to store custom collaborations
    });

    const [bestCollaborations, setBestCollaborations] = useState({
        first: '',
        second: '',
        customOptions: [] // Array to store custom options for dropdowns
    });

    // Add new state for barriers question
    const [innovationBarriers, setInnovationBarriers] = useState({
        rankings: {
            first: '',
            second: '',
            third: ''
        },
        other: '',
        customBarriers: []
    });

    // Add new state for innovation maturity
    const [innovationMaturity, setInnovationMaturity] = useState('');

    // Add new state for driver rankings
    const [driverRankings, setDriverRankings] = useState({
        first: '',
        second: '',
        third: '',
        fourth: '',
        fifth: ''
    });

    // Add new state for innovation vision
    const [hasInnovationVision, setHasInnovationVision] = useState('');

    // Add new state for market position
    const [marketPosition, setMarketPosition] = useState('');

    // Add new state for success measures
    const [successMeasures, setSuccessMeasures] = useState({
        first: '',
        second: '',
        third: ''
    });

    const [productStrategy, setProductStrategy] = useState({
        productDevelopment: 0,
        marketPenetration: 0,
        diversification: 0,
        marketDevelopment: 0
    });

    // Add new state for customer markets
    const [customerMarkets, setCustomerMarkets] = useState({
        first: '',
        second: '',
        third: ''
    });

    // Add new state for future markets
    const [futureMarkets, setFutureMarkets] = useState([]);

    // Add new state for Asian countries
    const [asianCountries, setAsianCountries] = useState({
        first: '',
        second: ''
    });

    // Add validation state
    const [validationState, setValidationState] = useState({
        email: false,
        option1: false,
        option2: false,
        option3: false,
        currentCollaborations: false,
        bestCollaborations: false,
        innovationBarriers: false,
        innovationMaturity: false,
        driverRankings: false,
        hasInnovationVision: false,
        marketPosition: false,
        successMeasures: false,
        productStrategy: false,
        priorityBuckets: false,
        customerMarkets: false,
        futureMarkets: false,
        asianCountries: false
    });

    // Add state to track if user has attempted to submit
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    // Add new state for loading
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Add useEffect to get and set the email
    useEffect(() => {
        const getAccessEmail = async () => {
            // First check URL params - try both formats
            const params = new URLSearchParams(window.location.search);
            const emailParam = params.get('f') || params.get(process.env.REACT_APP_API_KEY_PARAM);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            if (emailParam && emailRegex.test(emailParam)) {
                // setEmail(emailParam);
                setEmail('');
                return;
            }

            // Then check IndexedDB only if no valid URL param
            try {
                const request = indexedDB.open('IfiaDB', 1);

                request.onsuccess = (event) => {
                    const db = event.target.result;
                    const transaction = db.transaction(['auth'], 'readonly');
                    const store = transaction.objectStore('auth');
                    const getEmail = store.get('userEmail');

                    getEmail.onsuccess = () => {
                        if (getEmail.result && getEmail.result.email && emailRegex.test(getEmail.result.email)) {
                            setEmail(getEmail.result.email);
                        }
                    };
                };
            } catch (error) {
                console.error('Error accessing IndexedDB:', error);
            }
        };

        getAccessEmail();
    }, []); // Run once on component mount

    useEffect(() => {
        const loadData = async () => {
            try {
                // First try to load from IndexedDB
                const savedData = await loadFromIDB();

                if (savedData) {
                    setTopics(savedData.topics);
                    setBuckets(savedData.buckets);
                    setEmail(savedData.email || '');
                    setOption1(savedData.option1 || '');
                    setOption2(savedData.option2 || '');
                    setOption3(savedData.option3 || '');



                    // Set submission status if it exists
                    if (savedData.isSubmitted) {
                        setIsSubmitted(savedData.isSubmitted);
                    }

                    // Restore survey answers if they exist
                    if (savedData.surveyAnswers) {
                        const {
                            currentCollaborations: savedCollabs,
                            bestCollaborations: savedBest,
                            innovationBarriers: savedBarriers,
                            innovationMaturity: savedMaturity,
                            driverRankings: savedRankings,
                            hasInnovationVision: savedVision,
                            marketPosition: savedPosition,
                            successMeasures: savedMeasures,
                            productStrategy: savedStrategy,
                            customerMarkets: savedMarkets,
                            futureMarkets: savedFutureMarkets,
                            asianCountries: savedAsianCountries
                        } = savedData.surveyAnswers;

                        if (savedCollabs) setCurrentCollaborations(savedCollabs);
                        if (savedBest) setBestCollaborations(savedBest);
                        if (savedBarriers) setInnovationBarriers(savedBarriers);
                        if (savedMaturity) setInnovationMaturity(savedMaturity);
                        if (savedRankings) setDriverRankings(savedRankings);
                        if (savedVision) setHasInnovationVision(savedVision);
                        if (savedPosition) setMarketPosition(savedPosition);
                        if (savedMeasures) setSuccessMeasures(savedMeasures);
                        if (savedStrategy) setProductStrategy(savedStrategy);
                        if (savedMarkets) setCustomerMarkets(savedMarkets);
                        if (savedFutureMarkets) setFutureMarkets(savedFutureMarkets);
                        if (savedAsianCountries) setAsianCountries(savedAsianCountries);
                    }

                    setInitialData({
                        topics: savedData.topics,
                        buckets: savedData.buckets,
                        email: savedData.email,
                        option1: savedData.option1,
                        option2: savedData.option2,
                        option3: savedData.option3,
                        surveyAnswers: savedData.surveyAnswers,
                        isSubmitted: savedData.isSubmitted
                    });
                    setIdbLoaded(true);
                    return;
                }

                // If no saved data, load from JSON file
                const data = require('../../../data/driversofinnovation.json');
                // const data = await apiHelper.fetchJSONFileByNameAndContent('innovation.json');
                const processedData = {
                    topics: data || [],
                    buckets: {
                        ...buckets,
                        topicsBucket: (data || []).map(item => ({
                            id: item.ID.toString(),
                            content: item.Topic,
                            category: item.Category,
                            driver: item.DriversInnovation,
                            color: getColorForDriver(item.DriversInnovation)
                        }))
                    }
                };
                setTopics(processedData.topics);
                setBuckets(processedData.buckets);
                setInitialData(processedData);
                setIdbLoaded(true);
            } catch (error) {
                console.error('Error loading data:', error);
                toast.error('Error loading data. Please try again.');
            }
        };

        loadData();
    }, []);

    // Check for changes whenever buckets state changes
    useEffect(() => {
        if (initialData && idbLoaded) {
            const hasChangedBuckets = JSON.stringify(buckets) !== JSON.stringify(initialData.buckets);
            const hasChangedName = email !== initialData.email || option1 !== initialData.option1 ||
                option2 !== initialData.option2 ||
                option3 !== initialData.option3;

            setHasChanges(hasChangedBuckets || hasChangedName);
        }
    }, [buckets, email, option1, option2, option3, initialData, idbLoaded]);

    const handleCheckboxClick = (e, itemId) => {
        e.stopPropagation();
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    const onDragStart = (start) => {
        // Prevent dragging if survey is submitted
        if (isSubmitted) return;

        document.body.classList.add('dragging');
        let draggedItem;
        if (start.source.droppableId.startsWith('topicsBucket_')) {
            const driverType = start.source.droppableId.split('_')[1];
            const driverMap = {
                'businessOps': 'Business/Operations',
                'marketRegulation': 'Market&Regulation',
                'productMaintenance': 'Product/ServiceMaintenance&Improvement',
                'newProduct': 'NewProduct/ServiceDevelopment',
                'emerging': 'Emerging/Disruptive'
            };

            const filteredItems = buckets.topicsBucket?.filter(
                item => item?.driver === driverMap[driverType] &&
                    item.content.toLowerCase().includes(searchTerm.toLowerCase())
            ) || [];

            draggedItem = filteredItems[start.source.index];
        } else if (start.source.droppableId.endsWith('_p0')) {
            const mergedData = getMergedItemFromP0(
                start.source.droppableId,
                start.source.index,
                buckets,
                searchTerm
            );
            if (mergedData) {
                draggedItem = mergedData.draggedItem;
            }
        } else {
            draggedItem = buckets[start.source.droppableId][start.source.index];
        }

        if (draggedItem?.driver) {
            setActiveDriver(draggedItem.driver);
        } else {
            setActiveDriver(null);
        }
    };

    const onDragEnd = (result) => {
        // Prevent dropping if survey is submitted
        if (isSubmitted) return;

        document.body.classList.remove('dragging');
        setActiveDriver(null);
        const { source, destination } = result;

        if (!destination) return;

        // Prevent dragging within P0 pillars
        if (source.droppableId.endsWith('_p0') && destination.droppableId.endsWith('_p0')) {
            return;
        }

        // Get the driver type from the source and destination
        const sourceDriver = source.droppableId.split('_')[0];
        const destDriver = destination.droppableId.split('_')[0];

        // If moving from topicsBucket, get the item's driver
        const sourceItem = buckets[source.droppableId][source.index];
        const itemDriver = sourceItem?.driver?.toLowerCase().replace(/\s+/g, '');

        // Validate the drop is in the correct pillar
        if (source.droppableId !== 'topicsBucket' && sourceDriver !== destDriver) {
            toast.error('Items can only be moved within their respective pillars');
            return;
        }

        // If coming from topicsBucket, validate it's going to the correct pillar
        if (source.droppableId === 'topicsBucket') {
            const validDestination = {
                'businessoperations': 'businessOps',
                'marketregulation': 'marketRegulation',
                'productmaintenance': 'productMaintenance',
                'newproduct': 'newProduct',
                'emergingtech': 'emerging'
            }[itemDriver];

            if (destDriver !== validDestination) {
                toast.error('Items must be placed in their corresponding pillar');
                return;
            }
        }

        setBuckets(prev => {
            const newBuckets = { ...prev };

            // If source and destination are the same, this is a reorder within the same bucket
            if (source.droppableId === destination.droppableId) {
                const items = Array.from(prev[source.droppableId]);
                const [reorderedItem] = items.splice(source.index, 1);
                items.splice(destination.index, 0, reorderedItem);
                newBuckets[source.droppableId] = items;
                return newBuckets;
            }

            // Get the dragged item and source array
            let draggedItem;
            let sourceItems;

            if (source.droppableId.endsWith('_p0')) {
                const mergedData = getMergedItemFromP0(
                    source.droppableId,
                    source.index,
                    prev,
                    searchTerm
                );
                if (!mergedData) return prev;
                draggedItem = mergedData.draggedItem;

                // For P0, combine both arrays for proper item selection
                const driverType = source.droppableId.split('_')[0];
                const driver = driverMap[driverType];

                // Combine P0 items and filtered topicsBucket items
                sourceItems = [
                    ...(prev[source.droppableId] || []),
                    ...(prev.topicsBucket?.filter(item =>
                        item.driver === driver &&
                        item.content.toLowerCase().includes(searchTerm.toLowerCase())
                    ) || [])
                ];
            } else {
                draggedItem = prev[source.droppableId][source.index];
                sourceItems = prev[source.droppableId];
            }

            // Get all selected items if dragging a selected item
            let itemsToMove = [draggedItem];
            if (selectedItems.has(draggedItem.id)) {
                itemsToMove = sourceItems.filter(item => selectedItems.has(item.id));
            }

            // Remove items from their source locations
            if (source.droppableId.endsWith('_p0')) {
                // Remove from P0 bucket if items are there
                newBuckets[source.droppableId] = prev[source.droppableId]?.filter(
                    item => !itemsToMove.some(moveItem => moveItem.id === item.id)
                ) || [];

                // Remove from topicsBucket if items are there
                newBuckets.topicsBucket = prev.topicsBucket?.filter(
                    item => !itemsToMove.some(moveItem => moveItem.id === item.id)
                ) || [];
            } else {
                // Remove from source bucket
                newBuckets[source.droppableId] = prev[source.droppableId].filter(
                    item => !itemsToMove.some(moveItem => moveItem.id === item.id)
                );
            }

            // Add items to destination, ensuring no duplicates
            const destItems = Array.from(prev[destination.droppableId] || []);
            itemsToMove.forEach(item => {
                // Only add if item doesn't already exist in destination
                if (!destItems.some(destItem => destItem.id === item.id)) {
                    destItems.splice(destination.index, 0, item);
                }
            });
            newBuckets[destination.droppableId] = destItems;

            // Clear selection after moving
            setSelectedItems(new Set());

            return newBuckets;
        });
    };

    const getPriorityNumber = (bucketId) => {
        if (!bucketId) return '';
        if (bucketId.endsWith('_p0')) return '0';
        if (bucketId.endsWith('_p1')) return '1';
        if (bucketId.endsWith('_p2')) return '2';
        if (bucketId.endsWith('_p3')) return '3';
        return '';
    };

    const renderDraggableItem = (item, provided, snapshot) => {
        const isSelected = selectedItems.has(item.id);
        const selectedCount = selectedItems.size;
        const isDraggingSelected = snapshot.isDragging && isSelected && selectedCount > 1;

        // Find which bucket this item is in and its index
        const bucketEntry = Object.entries(buckets).find(([key, items]) =>
            items && items.some(i => i.id === item.id)
        );
        const bucketId = bucketEntry?.[0];
        const bucketItems = bucketEntry?.[1] || [];
        const itemIndex = bucketItems.findIndex(i => i.id === item.id);

        // Show position number if it's in a high priority bucket (p3) and within first 3 items
        const showPosition = bucketId && bucketId.endsWith('_p3') && itemIndex < 3;
        const positionNumber = showPosition ? itemIndex + 1 : '';

        const handleTopicClick = (e) => {
            // Don't open modal if clicking checkbox
            if (e.target.type === 'checkbox') return;

            // Find the full topic data from the topics array
            const topicData = topics.find(t => t.ID.toString() === item.id);
            if (topicData) {
                setSelectedTopicData({
                    title: topicData.Topic,
                    topic: topicData.Topic,
                    category: topicData.Category,
                    driver: topicData.DriversInnovation,
                    primaryBenefit: topicData.PrimaryBenefit,
                    description: topicData.Description,
                    definition: topicData.Definition
                });
                setShowTopicModal(true);
            }
        };

        return (
            <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                className={`draggable-item ${snapshot.isDragging ? 'dragging' : ''} ${item.isCustom ? 'custom-topic' : ''}`}
                style={{
                    ...provided.draggableProps.style,
                    borderRightColor: item.color
                }}
                onClick={handleTopicClick}
            >
                <div className="item-content">
                    <input
                        type="checkbox"
                        className="item-checkbox"
                        checked={isSelected}
                        onChange={(e) => handleCheckboxClick(e, item.id)}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <span className="topic-content">
                        {positionNumber && <span className="position-number" data-number={positionNumber}>{positionNumber}.</span>}
                        {item.content}
                    </span>
                </div>
                {item.isCustom && (
                    <>
                        <span className="custom-badge">Custom</span>
                        <button
                            className="delete-topic-button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTopic(item.id);
                            }}
                            title="Delete topic"
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#e74c3c',
                                cursor: 'pointer',
                                padding: '4px 8px',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                position: 'absolute',
                                right: '5px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                transition: 'all 0.2s ease',
                                opacity: '0.7',
                                marginLeft: '8px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.backgroundColor = '#fee2e2';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '0.7';
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            ×
                        </button>
                    </>
                )}
                {isDraggingSelected && (
                    <div className="multi-drag-indicator">
                        +{selectedCount - 1} more
                    </div>
                )}
            </div>
        );
    };

    const generateResultsHTML = () => {
        let html = `<!DOCTYPE html><html><head><title>Innovation Survey Results</title><meta charset="UTF-8"><style>body{font-family:Arial,sans-serif;line-height:1.6;color:#333;margin:0 auto;padding:0px}</style></head><body><div class="survey-results">`;

        // Header
        html += `
            <div style="text-align: center; margin-bottom: 50px; padding: 30px; background: linear-gradient(135deg, #56b32f 0%, #385724 100%); color: white; border-radius: 10px;">
                <h1 style="font-size: 32px; margin-bottom: 15px;">Innovation Survey Results</h1>
                <p style="font-size: 14px; opacity: 0.8;">Date: ${new Date().toLocaleDateString('en-GB')}</p>
            </div>
        `;

        // After the header section (around line 718), add:
        html += `
            <div style="text-align: center; margin-bottom: 30px; padding: 20px; background: #f0fdf4; border: 2px solid #56b32f; color: #385724; border-radius: 10px;">
                <h2 style="margin: 0; font-size: 24px;">✅ Survey Submitted Successfully!</h2>
                <p style="margin: 10px 0 0 0; font-size: 16px;">Thank you for completing the Innovation Survey.</p>
            </div>
        `;

        // Company Information
        html += `
            <div style="margin-bottom: 50px;">
                <h2 style="color: #385724; font-size: 24px; padding-bottom: 15px; border-bottom: 3px solid #385724; margin-bottom: 25px;">Company Information</h2>
                <div style="background: #f8f9fa; padding: 25px; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <p style="margin: 15px 0; font-size: 16px;"><strong style="color: #444;">Submitted By:</strong> <span style="margin-left: 10px;">${email}</span></p>
                    <p style="margin: 15px 0; font-size: 16px;"><strong style="color: #444;">FTE Count:</strong> <span style="margin-left: 10px;">${option1}</span></p>
                    <p style="margin: 15px 0; font-size: 16px;"><strong style="color: #444;">Company Structure:</strong> <span style="margin-left: 10px;">${option2}</span></p>
                    <p style="margin: 15px 0; font-size: 16px;"><strong style="color: #444;">Company Type:</strong> <span style="margin-left: 10px;">${option3}</span></p>
                </div>
            </div>
        `;

        // Add Customer Markets section before Product Strategy
        html += `
            <div style="margin-bottom: 50px;">
                <h2 style="color: #385724; font-size: 24px; padding-bottom: 15px; border-bottom: 3px solid #385724; margin-bottom: 25px;">Customer Markets</h2>
                <div style="background: #f8f9fa; padding: 25px; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <div style="display: grid; gap: 15px;">
                        <p style="margin: 0;"><strong style="color: #444;">Highest Revenue Market:</strong> <span style="margin-left: 10px;">${getMarketLabel(customerMarkets.first)}</span></p>
                        <p style="margin: 0;"><strong style="color: #444;">Second Highest Revenue Market:</strong> <span style="margin-left: 10px;">${getMarketLabel(customerMarkets.second)}</span></p>
                        <p style="margin: 0;"><strong style="color: #444;">Third Highest Revenue Market:</strong> <span style="margin-left: 10px;">${getMarketLabel(customerMarkets.third)}</span></p>
                    </div>
                </div>
            </div>
        `;

        // Innovation Strategy
        html += `
            <div style="margin-bottom: 50px;">
                <h2 style="color: #385724; font-size: 24px; padding-bottom: 15px; border-bottom: 3px solid #385724; margin-bottom: 25px;">Innovation Strategy</h2>
                <div style="background: #f8f9fa; padding: 25px; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <p style="margin: 15px 0; font-size: 16px;"><strong style="color: #444;">Innovation Vision Statement:</strong> <span style="margin-left: 10px;">${hasInnovationVision === 'yes' ? 'Yes' : 'No'}</span></p>
                    <p style="margin: 15px 0; font-size: 16px;"><strong style="color: #444;">Market Position:</strong> <span style="margin-left: 10px;">${getMarketPositionLabel(marketPosition)}</span></p>
                    <div style="margin-top: 25px; padding-top: 25px; border-top: 2px solid #e9ecef;">
                        <h3 style="color: #385724; font-size: 18px; margin-bottom: 15px;">Product Strategy Distribution</h3>
                        <div style="display: grid; gap: 15px;">
                            <p style="margin: 0;"><strong style="color: #444;">Product Development:</strong> <span style="margin-left: 10px;">${productStrategy.productDevelopment}%</span></p>
                            <p style="margin: 0;"><strong style="color: #444;">Market Penetration:</strong> <span style="margin-left: 10px;">${productStrategy.marketPenetration}%</span></p>
                            <p style="margin: 0;"><strong style="color: #444;">Diversification:</strong> <span style="margin-left: 10px;">${productStrategy.diversification}%</span></p>
                            <p style="margin: 0;"><strong style="color: #444;">Market Development:</strong> <span style="margin-left: 10px;">${productStrategy.marketDevelopment}%</span></p>
                            <p style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e9ecef; font-weight: bold; color: #385724;">Total: ${getTotalPercentage()}%</p>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Innovation Drivers & Barriers
        html += `
            <div style="margin-bottom: 50px;">
                <h2 style="color: #385724; font-size: 24px; padding-bottom: 15px; border-bottom: 3px solid #385724; margin-bottom: 25px;">Innovation Drivers & Barriers</h2>
                <div style="background: #f8f9fa; padding: 25px; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <h3 style="color: #385724; font-size: 18px; margin-bottom: 15px;">Budget Allocation Priorities</h3>
                    <div style="display: grid; gap: 15px;">
                        <p style="margin: 0;"><strong style="color: #444;">1st Priority:</strong> <span style="margin-left: 10px;">${getDriverLabel(driverRankings.first)}</span></p>
                        <p style="margin: 0;"><strong style="color: #444;">2nd Priority:</strong> <span style="margin-left: 10px;">${getDriverLabel(driverRankings.second)}</span></p>
                        <p style="margin: 0;"><strong style="color: #444;">3rd Priority:</strong> <span style="margin-left: 10px;">${getDriverLabel(driverRankings.third)}</span></p>
                        <p style="margin: 0;"><strong style="color: #444;">4th Priority:</strong> <span style="margin-left: 10px;">${getDriverLabel(driverRankings.fourth)}</span></p>
                        <p style="margin: 0;"><strong style="color: #444;">5th Priority:</strong> <span style="margin-left: 10px;">${getDriverLabel(driverRankings.fifth)}</span></p>
                    </div>
                    <div style="margin-top: 25px; padding-top: 25px; border-top: 2px solid #e9ecef;">
                        <h3 style="color: #385724; font-size: 18px; margin-bottom: 15px;">Innovation Barriers</h3>
                        <div style="display: grid; gap: 15px;">
                            <p style="margin: 0;"><strong style="color: #444;">Primary Barrier:</strong> <span style="margin-left: 10px;">${getBarrierLabel(innovationBarriers.rankings.first)}</span></p>
                            <p style="margin: 0;"><strong style="color: #444;">Secondary Barrier:</strong> <span style="margin-left: 10px;">${getBarrierLabel(innovationBarriers.rankings.second)}</span></p>
                            <p style="margin: 0;"><strong style="color: #444;">Tertiary Barrier:</strong> <span style="margin-left: 10px;">${getBarrierLabel(innovationBarriers.rankings.third)}</span></p>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Success Measures & Maturity
        html += `
            <div style="margin-bottom: 50px;">
                <h2 style="color: #385724; font-size: 24px; padding-bottom: 15px; border-bottom: 3px solid #385724; margin-bottom: 25px;">Success Measures & Maturity</h2>
                <div style="background: #f8f9fa; padding: 25px; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <h3 style="color: #385724; font-size: 18px; margin-bottom: 15px;">Success Measures</h3>
                    <div style="display: grid; gap: 15px;">
                        <p style="margin: 0;"><strong style="color: #444;">Primary Measure:</strong> <span style="margin-left: 10px;">${getSuccessMeasureLabel(successMeasures.first)}</span></p>
                        <p style="margin: 0;"><strong style="color: #444;">Secondary Measure:</strong> <span style="margin-left: 10px;">${getSuccessMeasureLabel(successMeasures.second)}</span></p>
                        <p style="margin: 0;"><strong style="color: #444;">Tertiary Measure:</strong> <span style="margin-left: 10px;">${getSuccessMeasureLabel(successMeasures.third)}</span></p>
                    </div>
                    <div style="margin-top: 25px; padding-top: 25px; border-top: 2px solid #e9ecef;">
                        <h3 style="color: #385724; font-size: 18px; margin-bottom: 15px;">Innovation Maturity</h3>
                        <p style="margin: 0;"><strong style="color: #444;">Current State:</strong> <span style="margin-left: 10px;">${getMaturityLabel(innovationMaturity)}</span></p>
                    </div>
                </div>
            </div>
        `;

        // Priority Topics by Pillar
        html += `
            <div style="margin-bottom: 50px;">
                <h2 style="color: #385724; font-size: 24px; padding-bottom: 15px; border-bottom: 3px solid #385724; margin-bottom: 25px;">Priority Topics by Pillar</h2>
                <div style="background: #f8f9fa; padding: 25px; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    ${Object.entries({
            businessOps: "Business Operations",
            marketRegulation: "Market & Regulation",
            productMaintenance: "Product/Service Maintenance",
            newProduct: "New Product/Service Development",
            emerging: "Emerging/Disruptive Technologies"
        }).map(([key, title]) => {
            const highPriorityItems = buckets[`${key}_p3`] || [];
            const mediumPriorityItems = buckets[`${key}_p2`] || [];
            const lowPriorityItems = buckets[`${key}_p1`] || [];
            const noPriorityItems = buckets[`${key}_p0`] || [];

            if (highPriorityItems.length > 0 || mediumPriorityItems.length > 0 || lowPriorityItems.length > 0 || noPriorityItems.length > 0) {
                return `
                                <div style="margin-bottom: 35px;">
                                    <h3 style="color: #385724; font-size: 20px; margin-bottom: 20px;">${title}</h3>
                                    
                                    ${highPriorityItems.length > 0 ? `
                                        <div style="margin-bottom: 25px;">
                                            <h4 style="color: #385724; font-size: 16px; margin-bottom: 15px;">High Priority</h4>
                                            <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                                                ${highPriorityItems.map((item, index) => `
                                                    <div style="margin: ${index === 0 ? '0' : '15px 0 0 0'}; padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #d4380d;">
                                                        <span style="color: #385724; font-weight: 600; margin-right: 12px;">${index + 1}.</span>
                                                        <span style="color: #333;">${item.content}</span>
                                                    </div>
                                                `).join('')}
                                            </div>
                                        </div>
                                    ` : ''}
                                    
                                    ${mediumPriorityItems.length > 0 ? `
                                        <div style="margin-bottom: 25px;">
                                            <h4 style="color: #385724; font-size: 16px; margin-bottom: 15px;">Medium Priority</h4>
                                            <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                                                ${mediumPriorityItems.map((item, index) => `
                                                    <div style="margin: ${index === 0 ? '0' : '15px 0 0 0'}; padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #faad14;">
                                                        <span style="color: #385724; font-weight: 600; margin-right: 12px;">${index + 1}.</span>
                                                        <span style="color: #333;">${item.content}</span>
                                                    </div>
                                                `).join('')}
                                            </div>
                                        </div>
                                    ` : ''}
                                    
                                    ${lowPriorityItems.length > 0 ? `
                                        <div style="margin-bottom: 25px;">
                                            <h4 style="color: #385724; font-size: 16px; margin-bottom: 15px;">Low Priority</h4>
                                            <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                                                ${lowPriorityItems.map((item, index) => `
                                                    <div style="margin: ${index === 0 ? '0' : '15px 0 0 0'}; padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #52c41a;">
                                                        <span style="color: #385724; font-weight: 600; margin-right: 12px;">${index + 1}.</span>
                                                        <span style="color: #333;">${item.content}</span>
                                                    </div>
                                                `).join('')}
                                            </div>
                                        </div>
                                    ` : ''}
                                    
                                    ${noPriorityItems.length > 0 ? `
                                        <div style="margin-bottom: 25px;">
                                            <h4 style="color: #385724; font-size: 16px; margin-bottom: 15px;">No Priority</h4>
                                            <div style="background: white; padding: 20px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);">
                                                ${noPriorityItems.map((item, index) => `
                                                    <div style="margin: ${index === 0 ? '0' : '15px 0 0 0'}; padding: 15px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #8c8c8c;">
                                                        <span style="color: #385724; font-weight: 600; margin-right: 12px;">${index + 1}.</span>
                                                        <span style="color: #333;">${item.content}</span>
                                                    </div>
                                                `).join('')}
                                            </div>
                                        </div>
                                    ` : ''}
                                </div>
                            `;
            }
            return '';
        }).join('')}
                </div>
            </div>
        `;

        // Add Future Markets section
        html += `
            <div style="margin-bottom: 50px;">
                <h2 style="color: #385724; font-size: 24px; padding-bottom: 15px; border-bottom: 3px solid #385724; margin-bottom: 25px;">Future Markets</h2>
                <div style="background: #f8f9fa; padding: 25px; border-radius: 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                    <div style="display: grid; gap: 15px;">
                        <p style="margin: 0;"><strong style="color: #444;">Target Markets (1-3 years):</strong></p>
                        <ul style="margin: 10px 0; padding-left: 20px;">
                            ${futureMarkets.map(market => `<li>${getMarketLabel(market)}</li>`).join('')}
                        </ul>
                        ${futureMarkets.includes('asia') ? `
                            <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e9ecef;">
                                <p style="margin: 0;"><strong style="color: #444;">Asian Countries of Interest:</strong></p>
                                <ul style="margin: 10px 0; padding-left: 20px;">
                                    <li>${asianCountries.first}</li>
                                    <li>${asianCountries.second}</li>
                                </ul>
                            </div>
                        ` : ''}
                    </div>
                </div>
            </div>
        `;

        html += '</div></body></html>';
        return html;
    };

    // Helper function to get barrier label
    const getBarrierLabel = (value) => {
        // If it's a custom barrier (starts with 'custom-')
        if (value?.startsWith('custom-')) {
            const index = parseInt(value.split('-')[1]);
            // Return just the custom barrier text without duplication
            return innovationBarriers.customBarriers[index] || value;
        }

        const barrierLabels = {
            'funding': 'Lack of appropriate Funding/Budget',
            'redtape': 'Too much internal red-tape e.g. risk & compliance',
            'regulation': 'Too much Regulation',
            'awareness': 'Lack of awareness of supports available - IDA/EI/grants etc',
            'localSkills': 'Don\'t have the prerequisite skills sets locally in the firm',
            'orgSkills': 'Don\'t have the prerequisite skills sets in the organisation, more generally',
            'leadership': 'Lack of senior sponsorship/leadership locally/internationally',
            'rdTeam': 'Sub-optimal/lack of R&D team with appropriate focus on innovation',
            'ecosystem': 'Lack of community (eco-system) innovation supports to nurture/support innovation more generally',
            'strategy': 'Lack of cohesive Innovation strategy/definition for our business'
        };
        return barrierLabels[value] || value;
    };

    // Helper function to get maturity label
    const getMaturityLabel = (value) => {
        const maturityLabels = {
            'wellEstablished': 'Well established, defined and understood in the organisation. Tied to budget, funding and resourced.',
            'notEstablished': 'Not established, defined and understood in the organisation',
            'couldBeBetter': 'Could be better defined to unlock untapped potential for the company'
        };
        return maturityLabels[value] || value;
    };

    // Helper function to get driver label
    const getDriverLabel = (value) => {
        const driverLabels = {
            'businessOps': 'Business Operations',
            'marketRegulation': 'Market & Regulations',
            'productMaintenance': 'Product/Service Maintenance',
            'newProduct': 'New Product/Service Development',
            'emerging': 'Emerging/Disruptive Technologies'
        };
        return driverLabels[value] || value;
    };

    // Helper function to get market position label
    const getMarketPositionLabel = (value) => {
        const positionLabels = {
            'marketLeader': 'Market Leader',
            'closeFollower': 'Close Follower',
            'lateFollower': 'Late Follower'
        };
        return positionLabels[value] || value;
    };

    // Helper function to get success measure label
    const getSuccessMeasureLabel = (value) => {
        const measureLabels = {
            'costReduction': 'Cost reduction - against existing spend',
            'costAvoidance': 'Cost avoidance - of potential future spend',
            'productivity': 'Improved Productivity',
            'automation': 'Increased automation',
            'newRevenues': 'New revenues from New products/services',
            'existingRevenues': 'Increased revenues against Existing product/service line',
            'riskReduction': 'Risk Reduction',
            'clientExperience': 'Enhanced Client/customer Experience',
            'employeeMorale': 'Improved employee morale/working conditions',
            'orgEffectiveness': 'Improved Organisational Effectiveness',
            'newMarkets': 'New Markets - New Revenues',
            'thoughtLeadership': 'Thought Leadership collateral'
        };
        return measureLabels[value] || value;
    };

    const handleSave = async (showToast = true) => {
        try {
            const dataToSave = {
                topics,
                buckets,
                email,
                option1,
                option2,
                option3,
                isSubmitted,  // Include submission status
                surveyAnswers: {
                    currentCollaborations,
                    bestCollaborations,
                    innovationBarriers,
                    innovationMaturity,
                    driverRankings,
                    hasInnovationVision,
                    marketPosition,
                    successMeasures,
                    productStrategy,
                    customerMarkets,
                    futureMarkets,
                    asianCountries
                }
            };

            await saveToIDB(dataToSave);
            setInitialData(dataToSave);
            setHasChanges(false);
            if (showToast) {
                toast.success('Progress saved successfully');
            }
        } catch (error) {
            console.error('Error in handleSave:', error);
            toast.error('Error saving data. Please try again.');
        }
    };

    // Add these functions within your component

    const validateForm = () => {
        // Basic validation for required fields
        console.log('Validating email:', email, isValidEmail(email));
        if (!email || !isValidEmail(email)) {
            return false;
        }

        // Company information validation
        console.log('Validating company info:', { option1, option2 });
        if (!option1 || !option2) {
            return false;
        }

        // Innovation strategy validation
        console.log('Validating innovation strategy:', { hasInnovationVision, marketPosition });
        if (!hasInnovationVision || !marketPosition) {
            return false;
        }

        // Product strategy validation
        const totalPercentage = getTotalPercentage();
        console.log('Validating product strategy total percentage:', totalPercentage);
        if (totalPercentage !== 100) {
            return false;
        }

        // Markets validation
        console.log('Validating markets:', customerMarkets);
        if (!customerMarkets.first || !customerMarkets.second || !customerMarkets.third) {
            return false;
        }

        // Driver rankings validation
        console.log('Validating driver rankings:', driverRankings);
        if (!driverRankings.first || !driverRankings.second || !driverRankings.third ||
            !driverRankings.fourth || !driverRankings.fifth) {
            return false;
        }

        // Innovation barriers validation
        console.log('Validating innovation barriers:', innovationBarriers.rankings);
        if (!innovationBarriers.rankings.first || !innovationBarriers.rankings.second ||
            !innovationBarriers.rankings.third) {
            return false;
        }

        // Success measures validation
        console.log('Validating success measures:', successMeasures);
        if (!successMeasures.first || !successMeasures.second || !successMeasures.third) {
            return false;
        }

        // Innovation maturity validation
        console.log('Validating innovation maturity:', innovationMaturity);
        if (!innovationMaturity) {
            return false;
        }

        console.log('All validations passed');
        return true;
    };

    const handleSendResults = async () => {
        let sharepointData;
        try {
            console.log("Saving data before sending results...");
            await handleSave(false);

            console.log("Getting token...");
            const token = await getToken();
            if (!token) {
                throw new Error('No token received');
            }
            console.log("Token received successfully");

            // Get all prioritized topics including Priority 0
            const prioritizedTopics = {};
            const driverTypes = {
                'businessOps': 'Business Operations',
                'marketRegulation': 'Market & Regulations',
                'productMaintenance': 'Product/Service Maintenance',
                'newProduct': 'New Product/Service Development',
                'emerging': 'Emerging/Disruptive Technologies'
            };

            // Process each driver type
            Object.entries(driverTypes).forEach(([key, driverLabel]) => {
                // Initialize empty arrays for each priority level
                prioritizedTopics[driverLabel] = {
                    'Priority 0': [],
                    'Priority 1': [],
                    'Priority 2': [],
                    'Priority 3': []
                };

                // Get Priority 0 items (combine items from p0 bucket and unassigned items)
                const p0Items = [
                    ...(buckets[`${key}_p0`] || []),
                    ...(buckets.topicsBucket?.filter(item =>
                        item.driver === driverLabel &&
                        // Check if this item is not in any priority bucket
                        !Object.keys(buckets)
                            .filter(bucketKey => bucketKey !== 'topicsBucket')
                            .some(bucketKey =>
                                buckets[bucketKey].some(bucketItem =>
                                    bucketItem.id === item.id
                                )
                            )
                    ) || [])
                ];

                // Get items from other priority buckets
                const p1Items = buckets[`${key}_p1`] || [];
                const p2Items = buckets[`${key}_p2`] || [];
                const p3Items = buckets[`${key}_p3`] || [];

                // Store all items with their priorities
                prioritizedTopics[driverLabel]['Priority 0'] = p0Items.map(item => item.content);
                prioritizedTopics[driverLabel]['Priority 1'] = p1Items.map(item => item.content);
                prioritizedTopics[driverLabel]['Priority 2'] = p2Items.map(item => item.content);
                prioritizedTopics[driverLabel]['Priority 3'] = p3Items.map(item => item.content);
            });

            // Update TopicsBucket to include only truly unassigned items
            const unassignedTopics = buckets.topicsBucket?.filter(item =>
                // Check if this item is not in any priority bucket
                !Object.keys(buckets)
                    .filter(bucketKey => bucketKey !== 'topicsBucket')
                    .some(bucketKey =>
                        buckets[bucketKey].some(bucketItem =>
                            bucketItem.id === item.id
                        )
                    )
            );

            // Add TopicsBucket to sharepointData
            sharepointData = {
                fields: {
                    Title: `Innovation Survey - ${email}`,
                    IndustrySector: ["Irish Funds"],

                    // Company Info
                    CompanyEmail: email,
                    FTECount: option1,
                    CompanyStructure: option2,
                    CompanyType: option3,

                    // Question 1: Current Collaborations
                    CurrentCollaborations: JSON.stringify({
                        ...currentCollaborations,
                        customCollaborations: currentCollaborations.customCollaborations || []
                    }),

                    // Question 2: Best Collaborations
                    BestCollaborations: JSON.stringify({
                        first: bestCollaborations.first ? getCollaborationLabel(bestCollaborations.first) : null,
                        second: bestCollaborations.second ? getCollaborationLabel(bestCollaborations.second) : null,
                        customOptions: bestCollaborations.customOptions
                    }),

                    // Question 3: Innovation Barriers
                    InnovationBarriers: JSON.stringify({
                        rankings: innovationBarriers.rankings,
                        other: innovationBarriers.other,
                        customBarriers: innovationBarriers.customBarriers || []
                    }),

                    // Question 4: Innovation Maturity
                    InnovationMaturity: innovationMaturity === 'wellEstablished' ? 'Well established, defined and understood in the organisation. Tied to budget, funding and resourced.' :
                        innovationMaturity === 'notEstablished' ? 'Not established, defined and understood in the organisation' :
                            innovationMaturity === 'couldBeBetter' ? 'Could be better defined to unlock untapped potential for the company' :
                                innovationMaturity,

                    // Question 5: Driver Rankings
                    DriversOfInnovation: JSON.stringify({
                        first: driverRankings.first,
                        second: driverRankings.second,
                        third: driverRankings.third,
                        fourth: driverRankings.fourth,
                        fifth: driverRankings.fifth
                    }),

                    // Question 6: Innovation Vision
                    InnovationVision: hasInnovationVision,

                    // Question 7: Market Position
                    MarketPosition: marketPosition,

                    // Question 8: Success Measures
                    SuccessMeasures: JSON.stringify({
                        first: getSuccessMeasureLabel(successMeasures.first),
                        second: getSuccessMeasureLabel(successMeasures.second),
                        third: getSuccessMeasureLabel(successMeasures.third)
                    }),

                    // Question 9: Product Strategy
                    ProductStrategy: JSON.stringify({
                        productDevelopment: productStrategy.productDevelopment,
                        marketPenetration: productStrategy.marketPenetration,
                        diversification: productStrategy.diversification,
                        marketDevelopment: productStrategy.marketDevelopment
                    }),

                    // Question 10: Customer Markets
                    CustomerMarkets: JSON.stringify({
                        first: getMarketLabel(customerMarkets.first),
                        second: getMarketLabel(customerMarkets.second),
                        third: getMarketLabel(customerMarkets.third)
                    }),

                    // Question 11: Future Markets
                    FutureMarkets: JSON.stringify(
                        futureMarkets.map(market => market === 'na' ? 'N/A' : getMarketLabel(market))
                    ),

                    // Question 12: Asian Countries
                    AsianCountries: JSON.stringify({
                        first: asianCountries.first || null,    // CORRECT: using object property
                        second: asianCountries.second || null,   // CORRECT: using object property
                    }),

                    // Prioritized Topics
                    PrioritizedTopics: JSON.stringify(prioritizedTopics),
                    TopicsBucket: JSON.stringify(unassignedTopics),
                    // Required fields from schema
                    InnovationType_x002f_Category: "Sustaining/Incremental",
                    Topic_x002f_ThemeName: "Innovation Survey Response",
                    PrimaryBenefitTypeToIndustry: getSuccessMeasureLabel(successMeasures.first),
                    // Metadata
                    SubmissionDate: new Date().toISOString(),
                    LastModifiedDate: new Date().toISOString()
                }
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/store-sharepoint`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    // Log request headers in development
                    ...process.env.NODE_ENV === 'development' && { 'X-Debug': 'true' }
                },
                body: JSON.stringify(sharepointData)
            });


            if (!response.ok) {
                const errorText = await response.text();
                console.error('SharePoint Error Response:', errorText);
                throw new Error(`Failed to store data in SharePoint: ${response.status} ${errorText}`);
            }

            const responseData = await response.json();
            console.log("Survey results sent successfully:", responseData);

        } catch (error) {
            console.error('Error in handleSendResults:', error);
            
            // Log error to SharePoint
            try {
                const token = await getToken();
                await fetch(`${process.env.REACT_APP_API_URL}/api/log-error`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        errorMessage: error.message || 'Unknown error in handleSendResults',
                        errorStack: error.stack,
                        context: {
                            function: 'handleSendResults',
                            sharepointData: sharepointData,
                            responseStatus: error.response?.status
                        },
                        userEmail: email,
                        processingStage: 'Survey Submission'
                    })
                });
            } catch (loggingError) {
                console.error('Failed to log error to SharePoint:', loggingError);
            }
            
            toast.error(error.message || 'Failed to send survey results');
            throw error;
        }
    };

    // Helper function for collaboration labels
    const getCollaborationLabel = (value) => {
        if (value?.startsWith('custom-')) {
            const index = parseInt(value.split('-')[1]);
            return bestCollaborations.customOptions[index];  // Return just the custom text
        }

        const collaborationLabels = {
            'researchPartnerships': 'Research partnerships',
            'thirdLevelUniversity': '3rd level university collaboration',
            'jointVentures': 'Joint ventures',
            'startupInvestments': 'Investment in start-ups'
        };
        return collaborationLabels[value] || value;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setHasAttemptedSubmit(true);
            toast.error('Please complete all required fields');
            return;
        }

        // Show the preview modal instead of sending immediately
        setShowResults(true);
    };

    // Single handleFinalSubmit function that combines both validations and submission
    const handleFinalSubmit = async () => {

        if (!email.trim() || !isValidEmail(email)) {
            toast.error("Please enter a valid email address");
            return;
        }

        try {

            if (!validateForm()) {
                setHasAttemptedSubmit(true);
                toast.error('Please complete all required fields');
                return;
            }

            setIsSubmitting(true);
            await handleSendResults();
            setIsSubmitted(true);
            setShowResults(false);

            // Save other survey data without the email
            const dataToSave = {
                topics,
                buckets,
                email,
                option1,
                option2,
                option3,
                isSubmitted: true,
                surveyAnswers: {
                    currentCollaborations,
                    bestCollaborations,
                    innovationBarriers,
                    innovationMaturity,
                    driverRankings,
                    hasInnovationVision,
                    marketPosition,
                    successMeasures,
                    productStrategy,
                    customerMarkets,
                    futureMarkets,
                    asianCountries
                }
            };

            await saveToIDB(dataToSave);
            setInitialData(dataToSave);
            toast.success('Survey submitted successfully!');
            setShowResults(true);
        } catch (error) {
            console.error('Error submitting survey:', error);
            toast.error('Failed to submit survey');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper function to validate email format
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleReset = () => {
        setShowResetDialog(true);
    };

    const confirmReset = async () => {
        try {
            // Delete data from DriversOfInnovationDB
            const db = await initDB();
            const transaction = db.transaction(['surveyData'], 'readwrite');
            const store = transaction.objectStore('surveyData');
            await store.delete('currentState');

            // Reset all state variables first
            setTopics([]);
            setBuckets({
                topicsBucket: [],
                businessOps_p0: [], businessOps_p1: [], businessOps_p2: [], businessOps_p3: [], businessOps_p4: [], businessOps_p5: [],
                marketRegulation_p0: [], marketRegulation_p1: [], marketRegulation_p2: [], marketRegulation_p3: [], marketRegulation_p4: [], marketRegulation_p5: [],
                productMaintenance_p0: [], productMaintenance_p1: [], productMaintenance_p2: [], productMaintenance_p3: [], productMaintenance_p4: [], productMaintenance_p5: [],
                newProduct_p0: [], newProduct_p1: [], newProduct_p2: [], newProduct_p3: [], newProduct_p4: [], newProduct_p5: [],
                emerging_p0: [], emerging_p1: [], emerging_p2: [], emerging_p3: [], emerging_p4: [], emerging_p5: []
            });
            setInitialData(null);
            setHasChanges(false);
            setIdbLoaded(true);
            setIsSubmitted(false);
            setOption1('');
            setOption2('');
            setOption3('');
            setCustomTopics([]);
            setSearchTerm('');
            setSelectedItems(new Set());

            // Reset survey answers
            setCurrentCollaborations({
                researchPartnerships: false,
                thirdLevelUniversity: false,
                jointVentures: false,
                startupInvestments: false,
                other: '',
                customCollaborations: []
            });
            setBestCollaborations({
                first: '',
                second: '',
                customOptions: []
            });
            setInnovationBarriers({
                rankings: { first: '', second: '', third: '' },
                other: '',
                customBarriers: []
            });
            setInnovationMaturity('');
            setDriverRankings({
                first: '', second: '', third: '', fourth: '', fifth: ''
            });
            setHasInnovationVision('');
            setMarketPosition('');
            setSuccessMeasures({
                first: '', second: '', third: ''
            });
            setProductStrategy({
                productDevelopment: 0,
                marketPenetration: 0,
                diversification: 0,
                marketDevelopment: 0
            });
            setCustomerMarkets({
                first: '', second: '', third: ''
            });
            setFutureMarkets([]);
            setAsianCountries({
                first: '',
                second: ''
            });

            // Get email from URL or IDB
            const params = new URLSearchParams(window.location.search);
            const emailParam = params.get(process.env.REACT_APP_API_KEY_PARAM);
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            if (emailParam && emailRegex.test(emailParam)) {
                setEmail('');
            } else {
                // Check IndexedDB for email
                const ifiaRequest = indexedDB.open('IfiaDB', 1);
                ifiaRequest.onsuccess = (event) => {
                    const db = event.target.result;
                    const transaction = db.transaction(['auth'], 'readonly');
                    const store = transaction.objectStore('auth');
                    const getEmail = store.get('userEmail');

                    getEmail.onsuccess = () => {
                        if (getEmail.result && getEmail.result.email && emailRegex.test(getEmail.result.email)) {
                            setEmail(getEmail.result.email);
                        } else {
                            setEmail('');
                        }
                    };

                    getEmail.onerror = () => {
                        setEmail('');
                    };
                };
            }

            // Load fresh topics data
            const data = require('../../../data/driversofinnovation.json');
            const processedData = {
                topics: data || [],
                buckets: {
                    topicsBucket: (data || []).map(item => ({
                        id: item.ID.toString(),
                        content: item.Topic,
                        category: item.Category,
                        driver: item.DriversInnovation,
                        color: getColorForDriver(item.DriversInnovation)
                    }))
                }
            };
            setTopics(processedData.topics);
            setBuckets(prev => ({
                ...prev,
                topicsBucket: processedData.buckets.topicsBucket
            }));

            setShowResetDialog(false);
            toast.success('Survey has been reset to initial state.');
        } catch (error) {
            console.error('Error resetting survey:', error);
            toast.error('Error resetting survey. Please try again.');
            setShowResetDialog(false);
        }
    };

    const getColorForDriver = (driver) => {
        switch (driver) {
            case 'Business/Operations':
                return 'var(--ifia-blue)';
            case 'Market&Regulation':
                return 'var(--ifia-green)';
            case 'Product/ServiceMaintenance&Improvement':
                return 'var(--ifia-lightblue)';
            case 'NewProduct/ServiceDevelopment':
                return 'var(--ifia-purple)';
            case 'Emerging/Disruptive':
                return 'var(--ifia-orange)';
            default:
                return '#000000';
        }
    };

    const getDroppableClassName = (droppableId, isDraggingOver) => {
        if (!activeDriver) return 'droppable-area';

        // Extract pillar name from droppable ID (e.g., "businessOps_p3" -> "businessOps")
        const pillarName = droppableId.split('_')[0];

        let isValidTarget = false;
        switch (activeDriver) {
            case 'Business/Operations':
                isValidTarget = pillarName === 'businessOps';
                break;
            case 'Market&Regulation':
                isValidTarget = pillarName === 'marketRegulation';
                break;
            case 'Product/ServiceMaintenance&Improvement':
                isValidTarget = pillarName === 'productMaintenance';
                break;
            case 'NewProduct/ServiceDevelopment':
                isValidTarget = pillarName === 'newProduct';
                break;
            case 'Emerging/Disruptive':
                isValidTarget = pillarName === 'emerging';
                break;
        }

        let className = 'droppable-area';
        if (isDraggingOver && isValidTarget) className += ' dragging-over';
        if (isValidTarget) className += ' valid-target';
        return className;
    };

    // Helper to find the correct item + source array if droppable is p0
    const driverMap = {
        businessOps: 'Business/Operations',
        marketRegulation: 'Market&Regulation',
        productMaintenance: 'Product/ServiceMaintenance&Improvement',
        newProduct: 'NewProduct/ServiceDevelopment',
        emerging: 'Emerging/Disruptive'
    };

    function getMergedItemFromP0(droppableId, index, buckets, searchTerm) {
        // E.g. "businessOps_p0" => "businessOps"
        const pillarKey = droppableId.split('_')[0];
        const driver = driverMap[pillarKey] || '';

        // Actual arrays from state
        const p0Array = buckets[droppableId] || [];
        const topicsArray = buckets.topicsBucket?.filter(
            item =>
                item.driver === driver &&
                item.content.toLowerCase().includes(searchTerm.toLowerCase())
        ) || [];

        // The combined items displayed in the UI
        const merged = [...p0Array, ...topicsArray];

        // The item the user actually dragged
        const draggedItem = merged[index];
        if (!draggedItem) return null;

        // Figure out whether it lives in p0Array or topicsBucket
        const inP0Index = p0Array.findIndex((x) => x.id === draggedItem.id);
        if (inP0Index !== -1) {
            // The item is physically in buckets[droppableId]
            return {
                draggedItem,
                sourceKey: droppableId,
                sourceIndex: inP0Index
            };
        } else {
            // Must be in topicsBucket
            const inTopicsIndex = buckets.topicsBucket.findIndex((x) => x.id === draggedItem.id);
            return {
                draggedItem,
                sourceKey: 'topicsBucket',
                sourceIndex: inTopicsIndex
            };
        }
    }

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const move = (source, destination, droppableSource, droppableDestination) => {
        const sourceClone = Array.from(source || []);
        const destClone = Array.from(destination || []);
        const result = {};

        // Get the item being dragged
        let removed;
        if (droppableSource.droppableId.endsWith('_p0')) {
            // If dragging from a Priority 0 slot
            const pillarType = droppableSource.droppableId.split('_')[0];
            const driverMap = {
                'businessOps': 'Business/Operations',
                'marketRegulation': 'Market&Regulation',
                'productMaintenance': 'Product/ServiceMaintenance&Improvement',
                'newProduct': 'NewProduct/ServiceDevelopment',
                'emerging': 'Emerging/Disruptive'
            };

            // Get all items in this Priority 0 slot
            const allItems = [
                ...(buckets[droppableSource.droppableId] || []),
                ...(buckets.topicsBucket?.filter(item =>
                    item.driver === driverMap[pillarType] &&
                    item.content.toLowerCase().includes(searchTerm.toLowerCase())
                ) || [])
            ];

            // Get the item at the source index
            removed = allItems[droppableSource.index];

            // If the item is from topicsBucket, remove it from there
            if (!buckets[droppableSource.droppableId]?.find(item => item.id === removed.id)) {
                const topicsBucketClone = Array.from(buckets.topicsBucket || []);
                const itemIndex = topicsBucketClone.findIndex(item => item.id === removed.id);
                if (itemIndex !== -1) {
                    topicsBucketClone.splice(itemIndex, 1);
                    result.topicsBucket = topicsBucketClone;
                }
            } else {
                // Remove from source Priority 0
                const sourceIndex = sourceClone.findIndex(item => item.id === removed.id);
                if (sourceIndex !== -1) {
                    sourceClone.splice(sourceIndex, 1);
                }
            }
        } else {
            [removed] = sourceClone.splice(droppableSource.index, 1);
        }

        // Add to destination
        destClone.splice(droppableDestination.index, 0, removed);

        // Update result object
        result[droppableSource.droppableId] = sourceClone;
        result[droppableDestination.droppableId] = destClone;

        return result;
    };

    // Add function to handle new topic addition
    const handleAddTopic = () => {
        if (!newTopic.trim()) {
            toast.error('Please enter a topic');
            return;
        }

        const newCustomTopic = {
            id: `custom-${Date.now()}`,
            content: newTopic,
            driver: activeAddPillar,
            color: getColorForDriver(activeAddPillar),
            isCustom: true
        };

        setCustomTopics(prev => [...prev, newCustomTopic]);
        setBuckets(prev => ({
            ...prev,
            topicsBucket: [...prev.topicsBucket, newCustomTopic]
        }));

        setNewTopic('');
        setShowAddTopicModal(false);
        setActiveAddPillar(null);
        toast.success('Topic added successfully');
    };

    // Add JSX for the add topic button and modal
    const renderAddTopicButton = (pillarKey, driver) => (
        <button
            className="add-topic-button"
            onClick={() => {
                setActiveAddPillar(driver);
                setShowAddTopicModal(true);
            }}
        >
            + Add Topic
        </button>
    );

    // Add handleDeleteTopic function
    const handleDeleteTopic = (topicId) => {
        // Remove from buckets state
        setBuckets(prev => {
            const newBuckets = { ...prev };
            // Remove from topicsBucket
            newBuckets.topicsBucket = newBuckets.topicsBucket.filter(item => item.id !== topicId);

            // Remove from all priority buckets
            Object.keys(newBuckets).forEach(key => {
                if (key !== 'topicsBucket') {
                    newBuckets[key] = newBuckets[key].filter(item => item.id !== topicId);
                }
            });

            return newBuckets;
        });

        // Remove from customTopics
        setCustomTopics(prev => prev.filter(topic => topic.id !== topicId));
        toast.success('Topic deleted successfully');
    };

    const handleSelectAllInPillar = (pillarKey, items) => {
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            const itemIds = items.map(item => item.id);

            // If all items in this pillar are already selected, unselect them
            const allSelected = itemIds.every(id => newSet.has(id));
            if (allSelected) {
                itemIds.forEach(id => newSet.delete(id));
            } else {
                // Otherwise, select all items
                itemIds.forEach(id => newSet.add(id));
            }
            return newSet;
        });
    };

    const handleExportToExcel = async () => {
        // Get the original JSON data
        // const data = await apiHelper.fetchJSONFileByNameAndContent('innovation.json');
        const data = require('../../../data/driversofinnovation.json');

        // Group data by Drivers Innovation
        const groupedData = {
            'Business/Operations': [],
            'Market&Regulation': [],
            'Product/ServiceMaintenance&Improvement': [],
            'NewProduct/ServiceDevelopment': [],
            'Emerging/Disruptive': []
        };

        data.forEach(item => {
            if (groupedData[item.DriversInnovation]) {
                groupedData[item.DriversInnovation].push(item);
            }
        });

        // Create workbook and add sheets for each driver
        const wb = XLSX.utils.book_new();

        Object.entries(groupedData).forEach(([driver, items]) => {
            // Format data for this driver's sheet
            const sheetData = items.map(item => ({
                "ID": item.ID,
                "Topic": item.Topic,
                "Priority": "Priority 0", // Default value
                "__EMPTY__": "← Select Priority (High/Medium/Low/Priority 0)" // Instructions
            }));

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(sheetData);

            // Set column widths
            ws['!cols'] = [
                { wch: 8 },   // ID
                { wch: 40 },  // Topic
                { wch: 15 },  // Priority
                { wch: 40 }   // Instructions
            ];

            // Add data validation for Priority column
            const range = { s: { r: 1, c: 3 }, e: { r: sheetData.length, c: 3 } };
            if (!ws['!dataValidation']) ws['!dataValidation'] = [];
            ws['!dataValidation'].push({
                sqref: XLSX.utils.encode_range(range),
                values: ['High', 'Medium', 'Low', 'Priority 0']
            });

            // Add the sheet to workbook with a clean name
            const sheetName = driver
                .replace(/[\/&]/g, ' ')  // Replace / and & with space
                .replace(/[^a-zA-Z0-9 ]/g, '')  // Remove any other special chars
                .substring(0, 31);  // Excel sheet names limited to 31 chars
            XLSX.utils.book_append_sheet(wb, ws, sheetName);
        });

        // Add instructions sheet
        const instructionsData = [{
            "Instructions": "How to use this template:",
            "": "1. Each sheet represents a different driver of innovation",
            "2": "2. For each topic, select a priority from the dropdown in the 'Priority' column",
            "3": "3. Choose from: High, Medium, Low, or Priority 0",
            "4": "4. Priority 0 means the topic is not prioritized",
            "5": "5. Save your changes and import the file back into the system"
        }];
        const wsInstructions = XLSX.utils.json_to_sheet(instructionsData);
        XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");

        // Generate and download the Excel file
        XLSX.writeFile(wb, "drivers_of_innovation_template.xlsx");
        toast.success('Excel template downloaded successfully! Use the dropdown in the Priority column to set priorities.');
    };

    const hasAnyData = () => {
        // Check if there's any meaningful data in the survey
        return email.trim() !== '' ||
            option1 !== '' ||
            option2 !== '' ||
            option3 !== '' ||
            Object.entries(buckets).some(([key, value]) => {
                return key !== 'topicsBucket' && Array.isArray(value) && value.length > 0;
            });
    };

    // Add validation function for percentages
    const validatePercentages = (newValue, field) => {
        const updatedStrategy = { ...productStrategy, [field]: parseInt(newValue) || 0 };
        const total = Object.values(updatedStrategy).reduce((sum, val) => sum + val, 0);
        return total <= 100;
    };

    // Add handler for percentage changes
    const handlePercentageChange = (e, field) => {
        const newValue = parseInt(e.target.value) || 0;
        if (validatePercentages(newValue, field)) {
            setProductStrategy(prev => ({ ...prev, [field]: newValue }));
        }
    };

    // Add getter for total percentage
    const getTotalPercentage = () => {
        return Object.values(productStrategy).reduce((sum, val) => sum + val, 0);
    };

    const handleDownloadResults = () => {
        const resultsHtml = generateResultsHTML();
        const blob = new Blob([resultsHtml], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${email.split('@')[0]}_Innovation_Survey_Results.html`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    // Update validateAllQuestions function
    const validateAllQuestions = () => {
        const errors = [];
        const newValidationState = { ...validationState };

        // Check if at least one topic is placed in any priority bucket for each pillar
        const pillars = {
            'businessOps': 'Business Operations',
            'marketRegulation': 'Market & Regulation',
            'productMaintenance': 'Product/Service Maintenance',
            'newProduct': 'New Product/Service Development',
            'emerging': 'Emerging/Disruptive Technologies'
        };

        const emptyPillars = [];
        let allPillarsHaveTopics = true;

        Object.entries(pillars).forEach(([pillarKey, pillarName]) => {
            const pillarBuckets = Object.entries(buckets).filter(([key]) =>
                key.startsWith(pillarKey) && (key.endsWith('_p1') || key.endsWith('_p2') || key.endsWith('_p3'))
            );
            const hasTopicsInPillar = pillarBuckets.some(([_, items]) => items.length > 0);

            if (!hasTopicsInPillar) {
                emptyPillars.push(pillarName);
                allPillarsHaveTopics = false;
            }
        });

        if (!allPillarsHaveTopics) {
            errors.push(`Please place at least one topic in each pillar. Missing topics in: ${emptyPillars.join(', ')}`);
            newValidationState.priorityBuckets = false;
        } else {
            newValidationState.priorityBuckets = true;
        }

        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim() || !emailRegex.test(email.trim())) {
            errors.push("Please enter a valid email address");
            newValidationState.email = false;
        } else {
            newValidationState.email = true;
        }

        // Validate dropdown selections
        if (!option1) {
            errors.push("FTE Count selection is required");
            newValidationState.option1 = false;
        } else {
            newValidationState.option1 = true;
        }

        if (!option2) {
            errors.push("Company Structure selection is required");
            newValidationState.option2 = false;
        } else {
            newValidationState.option2 = true;
        }

        if (!option3) {
            errors.push("Company Type selection is required");
            newValidationState.option3 = false;
        } else {
            newValidationState.option3 = true;
        }

        // Validate current collaborations
        const hasCollaborations = Object.values(currentCollaborations).some(val =>
            val === true || (typeof val === 'string' && val.trim() !== '') ||
            (Array.isArray(val) && val.length > 0)
        );
        if (!hasCollaborations) {
            errors.push("Please select at least one current collaboration");
            newValidationState.currentCollaborations = false;
        } else {
            newValidationState.currentCollaborations = true;
        }

        // Validate best collaborations
        if (!bestCollaborations.first || !bestCollaborations.second) {
            errors.push("Please select both first and second best collaborations");
            newValidationState.bestCollaborations = false;
        } else {
            newValidationState.bestCollaborations = true;
        }

        // Validate innovation barriers
        if (!innovationBarriers.rankings.first || !innovationBarriers.rankings.second || !innovationBarriers.rankings.third) {
            errors.push("Please rank all three innovation barriers");
            newValidationState.innovationBarriers = false;
        } else {
            newValidationState.innovationBarriers = true;
        }

        // Validate innovation maturity
        if (!innovationMaturity) {
            errors.push("Please select your innovation maturity level");
            newValidationState.innovationMaturity = false;
        } else {
            newValidationState.innovationMaturity = true;
        }

        // Validate driver rankings
        const { first, second, third, fourth, fifth } = driverRankings;
        if (!first || !second || !third || !fourth || !fifth) {
            errors.push("Please rank all five drivers");
            newValidationState.driverRankings = false;
        } else {
            newValidationState.driverRankings = true;
        }

        // Validate innovation vision
        if (!hasInnovationVision) {
            errors.push("Please indicate if you have an innovation vision");
            newValidationState.hasInnovationVision = false;
        } else {
            newValidationState.hasInnovationVision = true;
        }

        // Validate market position
        if (!marketPosition) {
            errors.push("Please select your market position");
            newValidationState.marketPosition = false;
        } else {
            newValidationState.marketPosition = true;
        }

        // Validate success measures
        if (!successMeasures.first || !successMeasures.second || !successMeasures.third) {
            errors.push("Please select all three success measures");
            newValidationState.successMeasures = false;
        } else {
            newValidationState.successMeasures = true;
        }

        // Validate product strategy percentages
        const total = getTotalPercentage();
        if (total !== 100) {
            errors.push(`Product strategy percentages must total 100% (currently ${total}%)`);
            newValidationState.productStrategy = false;
        } else {
            newValidationState.productStrategy = true;
        }

        // Validate customer markets
        if (!customerMarkets.first || !customerMarkets.second || !customerMarkets.third) {
            errors.push("Please select all three customer markets");
            newValidationState.customerMarkets = false;
        } else {
            newValidationState.customerMarkets = true;
        }

        // Validate future markets
        if (futureMarkets.length === 0) {
            errors.push("Please select at least one future market");
            newValidationState.futureMarkets = false;
        } else {
            newValidationState.futureMarkets = true;
        }

        // Validate Asian countries if Asia is selected
        if (futureMarkets.includes('asia') && (!asianCountries.first || !asianCountries.second)) {
            errors.push("Please enter both Asian countries of interest");
            newValidationState.asianCountries = false;
        } else if (!futureMarkets.includes('asia')) {
            newValidationState.asianCountries = true;
        } else {
            newValidationState.asianCountries = true;
        }

        setValidationState(newValidationState);

        // If there are errors, show them all in the toast
        if (errors.length > 0) {
            const errorMessage = errors.map(err => `• ${err}`).join('\n');
            toast.error(
                <div>
                    <strong>Please fix the following:</strong>
                    <div style={{ whiteSpace: 'pre-line', marginTop: '8px' }}>
                        {errorMessage}
                    </div>
                </div>,
                {
                    autoClose: false,
                    closeOnClick: true,
                    draggable: true
                }
            );
        }

        return errors;
    };

    // Add class to pillars container for validation styling
    const getPillarValidationClass = () => {
        if (!hasAttemptedSubmit) return '';

        const pillars = ['businessOps', 'marketRegulation', 'productMaintenance', 'newProduct', 'emerging'];
        const allPillarsHaveTopics = pillars.every(pillarKey => {
            const pillarBuckets = Object.entries(buckets).filter(([key]) =>
                key.startsWith(pillarKey) && (key.endsWith('_p1') || key.endsWith('_p2') || key.endsWith('_p3'))
            );
            return pillarBuckets.some(([_, items]) => items.length > 0);
        });

        return allPillarsHaveTopics ? 'validation-success' : 'validation-error';
    };

    // Add helper function to get market label
    const getMarketLabel = (value) => {
        const marketLabels = {
            'northAmerica': 'North America',
            'southAmerica': 'South America',
            'ireland': 'Ireland',
            'uk': 'UK',
            'europe': 'Europe (ex Ireland)',
            'asiaAustralia': 'Asia, Australia'
        };
        return marketLabels[value] || value;
    };

    // Add this helper function
    const fillTestData = () => {
        setEmail('test@example.com');
        setOption1('100-500');
        setOption2('subsidiary');
        setOption3('regulated');
        setCurrentCollaborations({
            researchPartnerships: true,
            thirdLevelUniversity: true,
            jointVentures: false,
            startupInvestments: false,
            other: '',
            customCollaborations: []
        });
        setBestCollaborations({
            first: 'researchPartnerships',
            second: 'thirdLevelUniversity',
            customOptions: []
        });
        setInnovationBarriers({
            rankings: {
                first: 'funding',
                second: 'redtape',
                third: 'regulation'
            },
            other: '',
            customBarriers: []
        });
        setInnovationMaturity('wellEstablished');
        setDriverRankings({
            first: 'businessOps',
            second: 'marketRegulation',
            third: 'productMaintenance',
            fourth: 'newProduct',
            fifth: 'emerging'
        });
        setHasInnovationVision('yes');
        setMarketPosition('marketLeader');
        setSuccessMeasures({
            first: 'costReduction',
            second: 'productivity',
            third: 'automation'
        });
        setProductStrategy({
            productDevelopment: 25,
            marketPenetration: 25,
            diversification: 25,
            marketDevelopment: 25
        });
        setCustomerMarkets({
            first: 'ireland',
            second: 'uk',
            third: 'europe'
        });
        setFutureMarkets(['northAmerica', 'asia']);
        setAsianCountries({
            first: 'Japan',
            second: 'Singapore'
        });
    };

    // Add this temporarily near your other buttons for testing
    {
        process.env.NODE_ENV === 'development' && (
            <button
                onClick={fillTestData}
                style={{
                    position: 'fixed',
                    bottom: '10px',
                    right: '10px',
                    padding: '8px',
                    background: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }}
            >
                Fill Test Data
            </button>
        )
    }

    // Add this test function
    const handleTestAuth = async () => {
        try {
            toast.info('Testing authentication...');

            // Use a test email since we need one for the signature
            const testEmail = 'test@example.com';
            const timestamp = Date.now();
            const signature = await generateRequestSignature(testEmail, timestamp);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get-token`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    email: testEmail,
                    timestamp,
                    signature
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to get token: ${response.status} ${errorText}`);
            }

            const data = await response.json();
            if (!data.token) {
                throw new Error('No token received');
            }

            toast.success('Authentication successful! Token received');
            console.log('Test completed successfully');

        } catch (error) {
            console.error('Auth test failed:', error);
            toast.error(`Auth test failed: ${error.message}`);
        }
    };

    return (
        <>
            {/* {process.env.NODE_ENV === 'development' && (
                <button 
                    onClick={handleTestAuth}
                    style={{
                        position: 'fixed',
                        top: '10px',
                        right: '10px',
                        zIndex: 1000,
                        padding: '8px 16px',
                        backgroundColor: '#ff4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Test Auth
                </button>
            )} */}

            <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <div className="survey-container">
                    <div className="survey-actions">
                        <div className="survey-actions-left">
                            <div className="name-input-container">
                                <div className="input-group">
                                    <input
                                        type="text"
                                        placeholder="Your Email..."
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className={`email-input ${hasAttemptedSubmit ? (email.trim() && isValidEmail(email) ? 'validation-success' : 'validation-error') : ''}`}
                                        disabled={isSubmitted}
                                    />
                                </div>
                            </div>

                            <div className="dropdown-container">
                                {Object.entries(dropdownOptions).map(([key, config]) => (
                                    <select
                                        key={key}
                                        value={key === 'fteCount' ? option1 : key === 'companyStructure' ? option2 : option3}
                                        onChange={(e) => {
                                            if (key === 'fteCount') setOption1(e.target.value);
                                            else if (key === 'companyStructure') setOption2(e.target.value);
                                            else setOption3(e.target.value);
                                        }}
                                        className={`filter-dropdown ${hasAttemptedSubmit ?
                                            ((key === 'fteCount' && !option1) ||
                                                (key === 'companyStructure' && !option2) ||
                                                (key === 'regulatoryStatus' && !option3) ? 'validation-error' : 'validation-success')
                                            : ''
                                            }`}
                                        disabled={isSubmitted}
                                    >
                                        <option value="">{config.label}</option>
                                        {config.options.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                ))}
                            </div>
                        </div>
                        <div className="survey-actions-right">
                            {/* Remove the old button location from the survey-actions-right div */}
                        </div>
                    </div>


                    <div className="survey-actions-buttons">
                        <div className="survey-actions-buttons-left">

                            {!isSubmitted && hasAnyData() && (
                                <>
                                    <button className="btn-save" onClick={handleSave}>
                                        <i className="fas fa-save"></i> Save Progress
                                    </button>

                                    <button className="btn-submit" onClick={async () => {
                                        await handleFinalSubmit();
                                        
                                    }}>
                                        <i className="fas fa-paper-plane"></i> Submit Survey
                                    </button>
                                    <button className="btn-reset" onClick={handleReset}>
                                        <i className="fas fa-undo"></i> Start Again
                                    </button>
                                </>
                            )}

                            {isSubmitted && (
                                <>
                                    <button className="btn-view-results" onClick={() => setShowResults(true)}>
                                        <i className="fas fa-chart-bar"></i> View Results
                                    </button>
                                    <button
                                        className="btn-submit"
                                        onClick={handleDownloadResults}
                                        style={{ backgroundColor: 'var(--ifia-blue)' }}
                                    >
                                        <i className="fas fa-download"></i> Download Results
                                    </button>
                                    <button className="btn-reset" onClick={handleReset}>
                                        <i className="fas fa-undo"></i> Start Again
                                    </button>
                                </>
                            )}




                        </div>
                        <button
                            className="btn-download-excel"
                            onClick={handleExportToExcel}
                            title="Download Excel template"
                        >
                            <i className="fas fa-file-excel"></i> Download Topics
                        </button>

                    </div>



                    <div className="innovation-header">
                        <h2>TYPES OF INNOVATION</h2>
                        <div className="innovation-types">
                            <div className="dataBox type incremental">
                                Incremental / Sustaining
                            </div>
                            <div className="dataBox type adjacent">
                                Adjacent
                            </div>
                            <div className="dataBox type emerging">
                                Emerging / Disruptive
                            </div>
                        </div>
                    </div>

                    <div className="drivers-section">
                        <h2>DRIVERS OF INNOVATION</h2>
                    </div>

                    <div className={`pillars-container ${getPillarValidationClass()}`}>
                        {Object.entries({
                            businessOps: {
                                title: "Business Operations",
                                driver: "Business/Operations"
                            },
                            marketRegulation: {
                                title: "Market & Regulation",
                                driver: "Market&Regulation"
                            },
                            productMaintenance: {
                                title: "Product/Service Maintenance",
                                driver: "Product/ServiceMaintenance&Improvement"
                            },
                            newProduct: {
                                title: "New Product/Service Dev",
                                driver: "NewProduct/ServiceDevelopment"
                            },
                            emerging: {
                                title: "Emerging/Disruptive",
                                driver: "Emerging/Disruptive"
                            }
                        }).map(([key, { title, driver }]) => (
                            <div key={key} className="pillar" data-pillar={key}>
                                <h3>
                                    <span>{title}</span>
                                </h3>
                                <div className="pillar-priorities">
                                    {Object.entries(buckets)
                                        .filter(([k]) => k.startsWith(key))
                                        .flatMap(([, items]) => items)
                                        .length > 0 && (
                                            <div className="select-all-container">
                                                <label className="select-all-label-top">

                                                    <input
                                                        type="checkbox"
                                                        className="select-all-checkbox"
                                                        checked={
                                                            Object.entries(buckets)
                                                                .filter(([k]) => k.startsWith(key))
                                                                .flatMap(([, items]) => items)
                                                                .every(item => selectedItems.has(item.id))
                                                        }
                                                        onChange={() => handleSelectAllInPillar(
                                                            key,
                                                            Object.entries(buckets)
                                                                .filter(([k]) => k.startsWith(key))
                                                                .flatMap(([, items]) => items)
                                                        )}
                                                    />Select All

                                                </label>
                                            </div>
                                        )}
                                    <div className="priority-slot">
                                        <h4>
                                            HIGH
                                        </h4>
                                        <Droppable
                                            droppableId={`${key}_p3`}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    className={getDroppableClassName(`${key}_p3`, snapshot.isDraggingOver)}
                                                    {...provided.droppableProps}
                                                >
                                                    {buckets[`${key}_p3`]?.map((item, index) => (
                                                        <Draggable key={item.id} draggableId={item.id} index={index}>
                                                            {(provided, snapshot) => renderDraggableItem(item, provided, snapshot)}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                    <div className="priority-slot">
                                        <h4>MEDIUM</h4>
                                        <Droppable droppableId={`${key}_p2`}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    className={getDroppableClassName(`${key}_p2`, snapshot.isDraggingOver)}
                                                    {...provided.droppableProps}
                                                >
                                                    {buckets[`${key}_p2`]?.map((item, index) => (
                                                        <Draggable key={item.id} draggableId={item.id} index={index}>
                                                            {(provided, snapshot) => renderDraggableItem(item, provided, snapshot)}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                    <div className="priority-slot">
                                        <h4>LOW</h4>
                                        <Droppable droppableId={`${key}_p1`}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    className={getDroppableClassName(`${key}_p1`, snapshot.isDraggingOver)}
                                                    {...provided.droppableProps}
                                                >
                                                    {buckets[`${key}_p1`]?.map((item, index) => (
                                                        <Draggable key={item.id} draggableId={item.id} index={index}>
                                                            {(provided, snapshot) => renderDraggableItem(item, provided, snapshot)}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="search-container">
                        <div className="search-download-wrapper">
                            <div className="search-input-container">
                                <input
                                    type="text"
                                    placeholder="Search topics..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input"
                                />
                            </div>
                            <div className="th-instruction-text-container">
                                <span className="th-instruction-text">TH = Thematic</span>
                                {/* <div className="drag-indicator-inline">
                                    <i className="fas fa-arrows-alt-v"></i>
                                    <span>Drag items up/down</span>
                                </div> */}
                            </div>
                            <span className="instruction-text">Click on a topic to get the description</span>
                        </div>
                    </div>

                    <div className="priority-zero-container">
                        {Object.entries({
                            businessOps: {
                                title: "Business Operations",
                                driver: "Business/Operations"
                            },
                            marketRegulation: {
                                title: "Market & Regulation",
                                driver: "Market&Regulation"
                            },
                            productMaintenance: {
                                title: "Product/Service Maintenance",
                                driver: "Product/ServiceMaintenance&Improvement"
                            },
                            newProduct: {
                                title: "New Product/Service Dev",
                                driver: "NewProduct/ServiceDevelopment"
                            },
                            emerging: {
                                title: "Emerging/Disruptive",
                                driver: "Emerging/Disruptive"
                            }
                        }).map(([key, { title, driver }]) => {
                            const items = [
                                ...(buckets[`${key}_p0`] || []),
                                ...(buckets?.topicsBucket?.filter(item =>
                                    item.driver === driver &&
                                    item.content.toLowerCase().includes(searchTerm.toLowerCase())
                                ) || [])
                            ];

                            return (
                                <div key={key} className="priority-slot priority-zero" data-pillar={key}>
                                    {!isSubmitted && (
                                        <div className="priority-zero-header">
                                            {items.length > 0 && (
                                                <label className="select-all-label">

                                                    <input
                                                        type="checkbox"
                                                        className="select-all-checkbox"
                                                        checked={items.length > 0 && items.every(item => selectedItems.has(item.id))}
                                                        onChange={() => handleSelectAllInPillar(key, items)}
                                                    />
                                                    Select All


                                                </label>
                                            )}
                                            <button
                                                className="add-topic-button-bucket"
                                                onClick={() => {
                                                    setActiveAddPillar(driver);
                                                    setShowAddTopicModal(true);
                                                }}
                                            >
                                                <span style={{ fontSize: '18px' }}>+</span> Add
                                            </button>
                                        </div>
                                    )}
                                    <div className="priority-zero-title">
                                        <h4>NO PRIORITY</h4>
                                    </div>
                                    <div className="drag-indicator">
                                        <i className="fas fa-arrows-alt-v"></i>
                                        <span>Drag items up/down</span>
                                    </div>
                                    <Droppable droppableId={`${key}_p0`}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                className={getDroppableClassName(`${key}_p0`, snapshot.isDraggingOver)}
                                                {...provided.droppableProps}
                                            >
                                                {items.length === 0 ? (
                                                    <div className="empty-message">No items</div>
                                                ) : (
                                                    items.map((item, index) => (
                                                        <Draggable key={item.id} draggableId={item.id} index={index}>
                                                            {(provided, snapshot) => renderDraggableItem(item, provided, snapshot)}
                                                        </Draggable>
                                                    ))
                                                )}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            );
                        })}
                    </div>

                </div>
            </DragDropContext>

            <div className="survey-questions">
                <div className="question-section">
                    <h3>Survey Questions</h3>

                    <div className="question-block">
                        <h4 className="required">1. What types of innovation related collaboration is your firm involved with today?</h4>
                        <p className="question-subtitle">
                            Select all collaboration relevant to your firm.
                            <span className="required-note">* Required</span>
                        </p>

                        <div className={`checkbox-group ${hasAttemptedSubmit && !Object.values(currentCollaborations).some(val => val === true) ?
                            'validation-error' : ''
                            }`}>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={currentCollaborations.researchPartnerships}
                                    onChange={(e) => setCurrentCollaborations(prev => ({
                                        ...prev,
                                        researchPartnerships: e.target.checked
                                    }))}
                                    disabled={isSubmitted}
                                />
                                Research partnerships (ex. 3rd level university collaborations)
                            </label>

                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={currentCollaborations.thirdLevelUniversity}
                                    onChange={(e) => setCurrentCollaborations(prev => ({
                                        ...prev,
                                        thirdLevelUniversity: e.target.checked
                                    }))}
                                    disabled={isSubmitted}
                                />
                                3rd level university collaboration
                            </label>

                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={currentCollaborations.jointVentures}
                                    onChange={(e) => setCurrentCollaborations(prev => ({
                                        ...prev,
                                        jointVentures: e.target.checked
                                    }))}
                                    disabled={isSubmitted}
                                />
                                Joint ventures
                            </label>

                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={currentCollaborations.startupInvestments}
                                    onChange={(e) => setCurrentCollaborations(prev => ({
                                        ...prev,
                                        startupInvestments: e.target.checked
                                    }))}
                                    disabled={isSubmitted}
                                />
                                Investment in start-ups
                            </label>

                            {/* Add custom collaborations as checkboxes */}
                            {currentCollaborations.customCollaborations.map((collaboration, index) => (
                                <label key={`custom-${index}`} className="checkbox-label custom-collaboration">
                                    <input
                                        type="checkbox"
                                        checked={currentCollaborations[`custom-${index}`] || false}
                                        onChange={(e) => setCurrentCollaborations(prev => ({
                                            ...prev,
                                            [`custom-${index}`]: e.target.checked
                                        }))}
                                        disabled={isSubmitted}
                                    />
                                    <span className="collaboration-text">
                                        {collaboration}
                                        <span className="custom-badge">Custom</span>
                                    </span>
                                </label>
                            ))}

                            <div className="other-input">
                                <label>Other:</label>
                                <div className="other-input-group">
                                    <input
                                        type="text"
                                        value={currentCollaborations.other}
                                        onChange={(e) => setCurrentCollaborations(prev => ({
                                            ...prev,
                                            other: e.target.value
                                        }))}
                                        placeholder="Please specify"
                                        disabled={isSubmitted}
                                    />
                                    <button
                                        className="btn-add-other"
                                        onClick={() => {
                                            if (currentCollaborations.other.trim()) {
                                                const newCollaboration = currentCollaborations.other.trim();

                                                // Add to both questions
                                                setCurrentCollaborations(prev => ({
                                                    ...prev,
                                                    other: '', // Clear the input
                                                    customCollaborations: [...prev.customCollaborations, newCollaboration],
                                                    [`custom-${prev.customCollaborations.length}`]: true // Set the new custom option as checked
                                                }));

                                                setBestCollaborations(prev => ({
                                                    ...prev,
                                                    customOptions: [...prev.customOptions, newCollaboration]
                                                }));

                                                toast.success('Collaboration type added successfully');
                                            }
                                        }}
                                        disabled={!currentCollaborations.other.trim() || isSubmitted}
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>2. Which of those collaborations have provided the best outcomes for your firm?</h4>
                        <p className="question-subtitle">Select Top 2 only in order (1 = best outcome, 2 = good outcome). You can select from both predefined and custom collaborations added in Question 1.</p>

                        <div className="ranking-group">
                            <div className="ranking-select">
                                <label>Best Outcome (1st):</label>
                                <select
                                    value={bestCollaborations.first}
                                    onChange={(e) => {
                                        if (e.target.value === bestCollaborations.second) {
                                            setBestCollaborations(prev => ({
                                                first: e.target.value,
                                                second: ''
                                            }));
                                        } else {
                                            setBestCollaborations(prev => ({
                                                ...prev,
                                                first: e.target.value
                                            }));
                                        }
                                    }}
                                    className={`${hasAttemptedSubmit ? (!bestCollaborations.first ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                >
                                    <option value="">Select First Best Collaboration</option>
                                    <option value="researchPartnerships">Research partnerships</option>
                                    <option value="thirdLevelUniversity">3rd level university collaboration</option>
                                    <option value="jointVentures">Joint ventures</option>
                                    <option value="startupInvestments">Investment in start-ups</option>
                                    {bestCollaborations.customOptions.map((option, index) => (
                                        <option key={`custom-${index}`} value={`custom-${index}`}>
                                            {option} (Custom)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="ranking-select">
                                <label>Good Outcome (2nd):</label>
                                <select
                                    value={bestCollaborations.second}
                                    onChange={(e) => setBestCollaborations(prev => ({
                                        ...prev,
                                        second: e.target.value
                                    }))}
                                    className={`${hasAttemptedSubmit ? (!bestCollaborations.second ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted || !bestCollaborations.first}
                                >
                                    <option value="">Select Second Best Collaboration</option>
                                    {[
                                        ["researchPartnerships", "Research partnerships"],
                                        ["thirdLevelUniversity", "3rd level university collaboration"],
                                        ["jointVentures", "Joint ventures"],
                                        ["startupInvestments", "Investment in start-ups"],
                                        ...bestCollaborations.customOptions.map((option, index) => [`custom-${index}`, `${option} (Custom)`])
                                    ].filter(([value]) => value !== bestCollaborations.first)
                                        .map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>3. Rank the top 3 biggest barriers in your firm that hinders innovation.</h4>
                        <p className="question-subtitle">Rank Top 3 barriers: 1 = Biggest Barrier and so on</p>

                        <div className="ranking-group barriers-ranking">
                            {/* First Barrier Selection */}
                            <div className="ranking-select">
                                <label>Biggest Barrier (1st):</label>
                                <select
                                    value={innovationBarriers.rankings.first}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setInnovationBarriers(prev => ({
                                            ...prev,
                                            rankings: {
                                                ...prev.rankings,
                                                first: newValue,
                                                // Clear other selections if they match the new selection
                                                second: prev.rankings.second === newValue ? '' : prev.rankings.second,
                                                third: prev.rankings.third === newValue ? '' : prev.rankings.third
                                            }
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ?
                                        (!innovationBarriers.rankings.first ? 'validation-error' : 'validation-success')
                                        : ''
                                        }`}
                                    disabled={isSubmitted}
                                >
                                    <option value="">Select First Barrier</option>
                                    <option value="funding">Lack of appropriate Funding/Budget</option>
                                    <option value="redtape">Too much internal red-tape e.g. risk & compliance</option>
                                    <option value="regulation">Too much Regulation</option>
                                    <option value="localSkills">Don't have the prerequisite skills sets locally in the firm</option>
                                    <option value="orgSkills">Don't have the prerequisite skills sets in the organisation, more generally</option>
                                    <option value="leadership">Lack of senior sponsorship/leadership locally/internationally</option>
                                    <option value="rdTeam">Sub-optimal/lack of R&D team with appropriate focus on innovation</option>
                                    <option value="ecosystem">Lack of community (eco-system) innovation supports to nurture/support innovation more generally</option>
                                    <option value="strategy">Lack of cohesive Innovation strategy/definition for our business</option>
                                    {innovationBarriers.customBarriers.map((barrier, index) => (
                                        <option key={`custom-${index}`} value={`custom-${index}`}>
                                            {barrier} (Custom)
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Second Barrier Selection */}
                            <div className="ranking-select">
                                <label>Second Biggest Barrier (2nd):</label>
                                <select
                                    value={innovationBarriers.rankings.second}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setInnovationBarriers(prev => ({
                                            ...prev,
                                            rankings: {
                                                ...prev.rankings,
                                                second: newValue,
                                                // Clear third if it matches the new selection
                                                third: prev.rankings.third === newValue ? '' : prev.rankings.third
                                            }
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ?
                                        (!innovationBarriers.rankings.second ? 'validation-error' : 'validation-success')
                                        : ''
                                        }`}
                                    disabled={isSubmitted || !innovationBarriers.rankings.first}
                                >
                                    <option value="">Select Second Barrier</option>
                                    {[
                                        ["funding", "Lack of appropriate Funding/Budget"],
                                        ["redtape", "Too much internal red-tape e.g. risk & compliance"],
                                        ["regulation", "Too much Regulation"],
                                        ["localSkills", "Don't have the prerequisite skills sets locally in the firm"],
                                        ["orgSkills", "Don't have the prerequisite skills sets in the organisation, more generally"],
                                        ["leadership", "Lack of senior sponsorship/leadership locally/internationally"],
                                        ["rdTeam", "Sub-optimal/lack of R&D team with appropriate focus on innovation"],
                                        ["ecosystem", "Lack of community (eco-system) innovation supports to nurture/support innovation more generally"],
                                        ["strategy", "Lack of cohesive Innovation strategy/definition for our business"],
                                        ...innovationBarriers.customBarriers.map((barrier, index) => [`custom-${index}`, `${barrier} (Custom)`])
                                    ].filter(([value]) => value !== innovationBarriers.rankings.first)
                                        .map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            {/* Third Barrier Selection */}
                            <div className="ranking-select">
                                <label>Third Biggest Barrier (3rd):</label>
                                <select
                                    value={innovationBarriers.rankings.third}
                                    onChange={(e) => {
                                        setInnovationBarriers(prev => ({
                                            ...prev,
                                            rankings: {
                                                ...prev.rankings,
                                                third: e.target.value
                                            }
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ?
                                        (!innovationBarriers.rankings.third ? 'validation-error' : 'validation-success')
                                        : ''
                                        }`}
                                    disabled={isSubmitted || !innovationBarriers.rankings.second}
                                >
                                    <option value="">Select Third Barrier</option>
                                    {[
                                        ["funding", "Lack of appropriate Funding/Budget"],
                                        ["redtape", "Too much internal red-tape e.g. risk & compliance"],
                                        ["regulation", "Too much Regulation"],
                                        ["localSkills", "Don't have the prerequisite skills sets locally in the firm"],
                                        ["orgSkills", "Don't have the prerequisite skills sets in the organisation, more generally"],
                                        ["leadership", "Lack of senior sponsorship/leadership locally/internationally"],
                                        ["rdTeam", "Sub-optimal/lack of R&D team with appropriate focus on innovation"],
                                        ["ecosystem", "Lack of community (eco-system) innovation supports to nurture/support innovation more generally"],
                                        ["strategy", "Lack of cohesive Innovation strategy/definition for our business"],
                                        ...innovationBarriers.customBarriers.map((barrier, index) => [`custom-${index}`, `${barrier} (Custom)`])
                                    ].filter(([value]) =>
                                        value !== innovationBarriers.rankings.first &&
                                        value !== innovationBarriers.rankings.second
                                    ).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Other Barrier Input */}
                            <div className="other-input">
                                <label>Other:</label>
                                <div className="other-input-group">
                                    <input
                                        type="text"
                                        value={innovationBarriers.other}
                                        onChange={(e) => setInnovationBarriers(prev => ({
                                            ...prev,
                                            other: e.target.value
                                        }))}
                                        placeholder="Please specify another barrier"
                                        disabled={isSubmitted}
                                    />
                                    <button
                                        className="btn-add-other"
                                        onClick={() => {
                                            if (innovationBarriers.other.trim()) {
                                                const newBarrier = innovationBarriers.other.trim();
                                                setInnovationBarriers(prev => ({
                                                    ...prev,
                                                    other: '', // Clear the input
                                                    customBarriers: [...prev.customBarriers, newBarrier]
                                                }));
                                                toast.success('Barrier added successfully');
                                            }
                                        }}
                                        disabled={!innovationBarriers.other.trim() || isSubmitted}
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>4. What is the current state of innovation maturity within your firm?</h4>
                        <p className="question-subtitle">Select one from the below answers that best fits your organisation.</p>

                        <div className={`radio-group ${hasAttemptedSubmit && !innovationMaturity ? 'validation-error' : ''
                            }`}>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="wellEstablished"
                                    checked={innovationMaturity === 'wellEstablished'}
                                    onChange={(e) => setInnovationMaturity(e.target.value)}
                                    className={`${hasAttemptedSubmit ? (!innovationMaturity ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                />
                                Well established, defined and understood in the organisation. Tied to budget, funding and resourced.
                            </label>

                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="notEstablished"
                                    checked={innovationMaturity === 'notEstablished'}
                                    onChange={(e) => setInnovationMaturity(e.target.value)}
                                    className={`${hasAttemptedSubmit ? (!innovationMaturity ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                />
                                Not established, defined and understood in the organisation
                            </label>

                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="couldBeBetter"
                                    checked={innovationMaturity === 'couldBeBetter'}
                                    onChange={(e) => setInnovationMaturity(e.target.value)}
                                    className={`${hasAttemptedSubmit ? (!innovationMaturity ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                />
                                Could be better defined to unlock untapped potential for the company
                            </label>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>5. Rank the relative importance of the 'Drivers of Innovation' within your domestic firm</h4>
                        <p className="question-subtitle">Rank from 1 to 5 based on the size of your firms allocated domestic budget to each, or equivalent metric that denotes total effort/output. 1 = The largest amount of the firms domestic budget is allocated to this driver, relative to the other drivers, and so on. Answers should correlate to the completed Innovation Radar above.</p>

                        <div className="ranking-group drivers-ranking">
                            {/* First Driver Selection */}
                            <div className="ranking-select">
                                <label>Largest Budget (1st):</label>
                                <select
                                    value={driverRankings.first}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setDriverRankings(prev => ({
                                            ...prev,
                                            first: newValue,
                                            second: prev.second === newValue ? '' : prev.second,
                                            third: prev.third === newValue ? '' : prev.third,
                                            fourth: prev.fourth === newValue ? '' : prev.fourth,
                                            fifth: prev.fifth === newValue ? '' : prev.fifth
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ?
                                        (!driverRankings.first ? 'validation-error' : 'validation-success')
                                        : ''
                                        }`}
                                    disabled={isSubmitted}
                                >
                                    <option value="">Select First Driver</option>
                                    <option value="businessOps">Business Operations</option>
                                    <option value="marketRegulation">Market & Regulations</option>
                                    <option value="productMaintenance">Product/Service Maintenance</option>
                                    <option value="newProduct">New Product/Service Development</option>
                                    <option value="emerging">Emerging/Disruptive Technologies</option>
                                </select>
                            </div>

                            {/* Second Driver Selection */}
                            <div className="ranking-select">
                                <label>Second Largest Budget (2nd):</label>
                                <select
                                    value={driverRankings.second}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setDriverRankings(prev => ({
                                            ...prev,
                                            second: newValue,
                                            third: prev.third === newValue ? '' : prev.third,
                                            fourth: prev.fourth === newValue ? '' : prev.fourth,
                                            fifth: prev.fifth === newValue ? '' : prev.fifth
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ?
                                        (!driverRankings.second ? 'validation-error' : 'validation-success')
                                        : ''
                                        }`}
                                    disabled={isSubmitted || !driverRankings.first}
                                >
                                    <option value="">Select Second Driver</option>
                                    {[
                                        ["businessOps", "Business Operations"],
                                        ["marketRegulation", "Market & Regulations"],
                                        ["productMaintenance", "Product/Service Maintenance"],
                                        ["newProduct", "New Product/Service Development"],
                                        ["emerging", "Emerging/Disruptive Technologies"]
                                    ].filter(([value]) => value !== driverRankings.first)
                                        .map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            {/* Third Driver Selection */}
                            <div className="ranking-select">
                                <label>Third Largest Budget (3rd):</label>
                                <select
                                    value={driverRankings.third}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setDriverRankings(prev => ({
                                            ...prev,
                                            third: newValue,
                                            fourth: prev.fourth === newValue ? '' : prev.fourth,
                                            fifth: prev.fifth === newValue ? '' : prev.fifth
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ?
                                        (!driverRankings.third ? 'validation-error' : 'validation-success')
                                        : ''
                                        }`}
                                    disabled={isSubmitted || !driverRankings.second}
                                >
                                    <option value="">Select Third Driver</option>
                                    {[
                                        ["businessOps", "Business Operations"],
                                        ["marketRegulation", "Market & Regulations"],
                                        ["productMaintenance", "Product/Service Maintenance"],
                                        ["newProduct", "New Product/Service Development"],
                                        ["emerging", "Emerging/Disruptive Technologies"]
                                    ].filter(([value]) =>
                                        value !== driverRankings.first &&
                                        value !== driverRankings.second
                                    ).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Fourth Driver Selection */}
                            <div className="ranking-select">
                                <label>Fourth Largest Budget (4th):</label>
                                <select
                                    value={driverRankings.fourth}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setDriverRankings(prev => ({
                                            ...prev,
                                            fourth: newValue,
                                            fifth: prev.fifth === newValue ? '' : prev.fifth
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ?
                                        (!driverRankings.fourth ? 'validation-error' : 'validation-success')
                                        : ''
                                        }`}
                                    disabled={isSubmitted || !driverRankings.third}
                                >
                                    <option value="">Select Fourth Driver</option>
                                    {[
                                        ["businessOps", "Business Operations"],
                                        ["marketRegulation", "Market & Regulations"],
                                        ["productMaintenance", "Product/Service Maintenance"],
                                        ["newProduct", "New Product/Service Development"],
                                        ["emerging", "Emerging/Disruptive Technologies"]
                                    ].filter(([value]) =>
                                        value !== driverRankings.first &&
                                        value !== driverRankings.second &&
                                        value !== driverRankings.third
                                    ).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Fifth Driver Selection */}
                            <div className="ranking-select">
                                <label>Fifth Largest Budget (5th):</label>
                                <select
                                    value={driverRankings.fifth}
                                    onChange={(e) => {
                                        setDriverRankings(prev => ({
                                            ...prev,
                                            fifth: e.target.value
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ?
                                        (!driverRankings.fifth ? 'validation-error' : 'validation-success')
                                        : ''
                                        }`}
                                    disabled={isSubmitted || !driverRankings.fourth}
                                >
                                    <option value="">Select Fifth Driver</option>
                                    {[
                                        ["businessOps", "Business Operations"],
                                        ["marketRegulation", "Market & Regulations"],
                                        ["productMaintenance", "Product/Service Maintenance"],
                                        ["newProduct", "New Product/Service Development"],
                                        ["emerging", "Emerging/Disruptive Technologies"]
                                    ].filter(([value]) =>
                                        value !== driverRankings.first &&
                                        value !== driverRankings.second &&
                                        value !== driverRankings.third &&
                                        value !== driverRankings.fourth
                                    ).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>6. Does your firm have a clearly articulated vision statement for innovation?</h4>
                        <p className="question-subtitle">For example: creating sustainable customer value & loyalty through innovation</p>

                        <div className={`radio-group ${hasAttemptedSubmit && !hasInnovationVision ? 'validation-error' : ''
                            }`}>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="yes"
                                    checked={hasInnovationVision === 'yes'}
                                    onChange={(e) => setHasInnovationVision(e.target.value)}
                                    className={`${hasAttemptedSubmit ? (!hasInnovationVision ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                />
                                Yes
                            </label>

                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="no"
                                    checked={hasInnovationVision === 'no'}
                                    onChange={(e) => setHasInnovationVision(e.target.value)}
                                    className={`${hasAttemptedSubmit ? (!hasInnovationVision ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                />
                                No
                            </label>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>7. Would you consider your firm to be a Market Leader, Close Follower or Late Follower when it comes to implementing new technologies?</h4>
                        <p className="question-subtitle">Select the option that best describes your firm's position in the market</p>

                        <div className={`radio-group ${hasAttemptedSubmit && !marketPosition ? 'validation-error' : ''
                            }`}>
                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="marketLeader"
                                    checked={marketPosition === 'marketLeader'}
                                    onChange={(e) => setMarketPosition(e.target.value)}
                                    className={`${hasAttemptedSubmit ? (!marketPosition ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                />
                                Market Leader
                            </label>

                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="closeFollower"
                                    checked={marketPosition === 'closeFollower'}
                                    onChange={(e) => setMarketPosition(e.target.value)}
                                    className={`${hasAttemptedSubmit ? (!marketPosition ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                />
                                Close Follower
                            </label>

                            <label className="radio-label">
                                <input
                                    type="radio"
                                    value="lateFollower"
                                    checked={marketPosition === 'lateFollower'}
                                    onChange={(e) => setMarketPosition(e.target.value)}
                                    className={`${hasAttemptedSubmit ? (!marketPosition ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                />
                                Late Follower
                            </label>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>8. Rank the top 3 most commonly used measures of success at your firm when assessing projects</h4>
                        <p className="question-subtitle">Answers should correlate to the list of topics/initiatives provided in the innovation radar above. 1 = the measure mostly frequently used at our firm, 2 = a measure frequently used, 3 = a measure used</p>

                        <div className="ranking-group measures-ranking">
                            {/* First Measure Selection */}
                            <div className="ranking-select">
                                <label>Most Frequently Used (1st):</label>
                                <select
                                    value={successMeasures.first}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setSuccessMeasures(prev => ({
                                            ...prev,
                                            first: newValue,
                                            second: prev.second === newValue ? '' : prev.second,
                                            third: prev.third === newValue ? '' : prev.third
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ? (!successMeasures.first ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted}
                                >
                                    <option value="">Select measure</option>
                                    <option value="costReduction">Cost reduction - against existing spend</option>
                                    <option value="costAvoidance">Cost avoidance - of potential future spend</option>
                                    <option value="productivity">Improved Productivity</option>
                                    <option value="automation">Increased automation</option>
                                    <option value="newRevenues">New revenues from New products/services</option>
                                    <option value="existingRevenues">Increased revenues against Existing product/service line</option>
                                    <option value="riskReduction">Risk Reduction</option>
                                    <option value="clientExperience">Enhanced Client/customer Experience</option>
                                    <option value="employeeMorale">Improved employee morale/working conditions</option>
                                    <option value="orgEffectiveness">Improved Organisational Effectiveness</option>
                                    <option value="newMarkets">New Markets - New Revenues</option>
                                    <option value="thoughtLeadership">Thought Leadership collateral</option>
                                </select>
                            </div>

                            {/* Second Measure Selection */}
                            <div className="ranking-select">
                                <label>Frequently Used (2nd):</label>
                                <select
                                    value={successMeasures.second}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setSuccessMeasures(prev => ({
                                            ...prev,
                                            second: newValue,
                                            third: prev.third === newValue ? '' : prev.third
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ? (!successMeasures.second ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted || !successMeasures.first}
                                >
                                    <option value="">Select measure</option>
                                    {[
                                        ["costReduction", "Cost reduction - against existing spend"],
                                        ["costAvoidance", "Cost avoidance - of potential future spend"],
                                        ["productivity", "Improved Productivity"],
                                        ["automation", "Increased automation"],
                                        ["newRevenues", "New revenues from New products/services"],
                                        ["existingRevenues", "Increased revenues against Existing product/service line"],
                                        ["riskReduction", "Risk Reduction"],
                                        ["clientExperience", "Enhanced Client/customer Experience"],
                                        ["employeeMorale", "Improved employee morale/working conditions"],
                                        ["orgEffectiveness", "Improved Organisational Effectiveness"],
                                        ["newMarkets", "New Markets - New Revenues"],
                                        ["thoughtLeadership", "Thought Leadership collateral"]
                                    ].filter(([value]) => value !== successMeasures.first)
                                        .map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            {/* Third Measure Selection */}
                            <div className="ranking-select">
                                <label>Used (3rd):</label>
                                <select
                                    value={successMeasures.third}
                                    onChange={(e) => {
                                        setSuccessMeasures(prev => ({
                                            ...prev,
                                            third: e.target.value
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ? (!successMeasures.third ? 'validation-error' : 'validation-success') : ''
                                        }`}
                                    disabled={isSubmitted || !successMeasures.second}
                                >
                                    <option value="">Select measure</option>
                                    {[
                                        ["costReduction", "Cost reduction - against existing spend"],
                                        ["costAvoidance", "Cost avoidance - of potential future spend"],
                                        ["productivity", "Improved Productivity"],
                                        ["automation", "Increased automation"],
                                        ["newRevenues", "New revenues from New products/services"],
                                        ["existingRevenues", "Increased revenues against Existing product/service line"],
                                        ["riskReduction", "Risk Reduction"],
                                        ["clientExperience", "Enhanced Client/customer Experience"],
                                        ["employeeMorale", "Improved employee morale/working conditions"],
                                        ["orgEffectiveness", "Improved Organisational Effectiveness"],
                                        ["newMarkets", "New Markets - New Revenues"],
                                        ["thoughtLeadership", "Thought Leadership collateral"]
                                    ].filter(([value]) =>
                                        value !== successMeasures.first &&
                                        value !== successMeasures.second
                                    ).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>9. Product Strategy Categories: When considering your domestic product/service strategy over the next 1-3yrs what percentage of your initiatives are focused in each of the following product strategy categories. </h4>
                        <p className="question-subtitle">(must total 100% across the four categories)</p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                            <div className="percentage-input-group">
                                <label>Product Development: Developing New Products/Services for sale into your Existing Markets/Client Segments</label>
                                <div className={`percentage-input-wrapper ${hasAttemptedSubmit ? (productStrategy.productDevelopment === 0 ? 'validation-error' : getTotalPercentage() === 100 ? 'validation-success' : 'validation-error') : ''}`}>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={productStrategy.productDevelopment}
                                        onChange={(e) => handlePercentageChange(e, 'productDevelopment')}
                                        disabled={isSubmitted}
                                    />
                                    <span className="percentage-symbol">%</span>
                                </div>
                            </div>

                            <div className="percentage-input-group">
                                <label>Market Penetration: Selling your Existing Products/Services into your Existing Markets/Client Segments</label>
                                <div className={`percentage-input-wrapper ${hasAttemptedSubmit ? (productStrategy.marketPenetration === 0 ? 'validation-error' : getTotalPercentage() === 100 ? 'validation-success' : 'validation-error') : ''}`}>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={productStrategy.marketPenetration}
                                        onChange={(e) => handlePercentageChange(e, 'marketPenetration')}
                                        disabled={isSubmitted}
                                    />
                                    <span className="percentage-symbol">%</span>
                                </div>
                            </div>

                            <div className="percentage-input-group">
                                <label>Diversification: Creating New Products/Services for sale into New Markets/Client Segments</label>
                                <div className={`percentage-input-wrapper ${hasAttemptedSubmit ? (productStrategy.diversification === 0 ? 'validation-error' : getTotalPercentage() === 100 ? 'validation-success' : 'validation-error') : ''}`}>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={productStrategy.diversification}
                                        onChange={(e) => handlePercentageChange(e, 'diversification')}
                                        disabled={isSubmitted}
                                    />
                                    <span className="percentage-symbol">%</span>
                                </div>
                            </div>

                            <div className="percentage-input-group">
                                <label>Market Development: Selling your Existing Products/Services into New Markets/Client Segments</label>
                                <div className={`percentage-input-wrapper ${hasAttemptedSubmit ? (productStrategy.marketDevelopment === 0 ? 'validation-error' : getTotalPercentage() === 100 ? 'validation-success' : 'validation-error') : ''}`}>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={productStrategy.marketDevelopment}
                                        onChange={(e) => handlePercentageChange(e, 'marketDevelopment')}
                                        disabled={isSubmitted}
                                    />
                                    <span className="percentage-symbol">%</span>
                                </div>
                            </div>

                            <div className={`total-percentage ${hasAttemptedSubmit ? (getTotalPercentage() === 100 ? 'success' : 'error') : ''}`}>
                                Total: {getTotalPercentage()}%
                                {hasAttemptedSubmit && getTotalPercentage() !== 100 && (
                                    <span className="validation-indicator error">
                                        <i className="fas fa-exclamation-circle"></i>
                                        Must equal 100%
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>10. What are your top three customer markets/regions today, using customer revenues as the measure?</h4>
                        <p className="question-subtitle">Select your top 3 markets in order of revenue (1 = highest revenue, 2 = second highest, 3 = third highest)</p>

                        <div className="ranking-group markets-ranking">
                            {/* First Market Selection */}
                            <div className="ranking-select">
                                <label>Highest Revenue (1st):</label>
                                <select
                                    value={customerMarkets.first}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setCustomerMarkets(prev => ({
                                            ...prev,
                                            first: newValue,
                                            second: prev.second === newValue ? '' : prev.second,
                                            third: prev.third === newValue ? '' : prev.third
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ? (!customerMarkets.first ? 'validation-error' : 'validation-success') : ''}`}
                                    disabled={isSubmitted}
                                >
                                    <option value="">Select market</option>
                                    <option value="northAmerica">North America</option>
                                    <option value="southAmerica">South America</option>
                                    <option value="ireland">Ireland</option>
                                    <option value="uk">UK</option>
                                    <option value="europe">Europe (ex Ireland)</option>
                                    <option value="asiaAustralia">Asia, Australia</option>
                                </select>
                            </div>

                            {/* Second Market Selection */}
                            <div className="ranking-select">
                                <label>Second Highest Revenue (2nd):</label>
                                <select
                                    value={customerMarkets.second}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setCustomerMarkets(prev => ({
                                            ...prev,
                                            second: newValue,
                                            third: prev.third === newValue ? '' : prev.third
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ? (!customerMarkets.second ? 'validation-error' : 'validation-success') : ''}`}
                                    disabled={isSubmitted || !customerMarkets.first}
                                >
                                    <option value="">Select market</option>
                                    {[
                                        ["northAmerica", "North America"],
                                        ["southAmerica", "South America"],
                                        ["ireland", "Ireland"],
                                        ["uk", "UK"],
                                        ["europe", "Europe (ex Ireland)"],
                                        ["asiaAustralia", "Asia, Australia"]
                                    ].filter(([value]) => value !== customerMarkets.first)
                                        .map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            {/* Third Market Selection */}
                            <div className="ranking-select">
                                <label>Third Highest Revenue (3rd):</label>
                                <select
                                    value={customerMarkets.third}
                                    onChange={(e) => {
                                        setCustomerMarkets(prev => ({
                                            ...prev,
                                            third: e.target.value
                                        }));
                                    }}
                                    className={`${hasAttemptedSubmit ? (!customerMarkets.third ? 'validation-error' : 'validation-success') : ''}`}
                                    disabled={isSubmitted || !customerMarkets.second}
                                >
                                    <option value="">Select market</option>
                                    {[
                                        ["northAmerica", "North America"],
                                        ["southAmerica", "South America"],
                                        ["ireland", "Ireland"],
                                        ["uk", "UK"],
                                        ["europe", "Europe (ex Ireland)"],
                                        ["asiaAustralia", "Asia, Australia"]
                                    ].filter(([value]) =>
                                        value !== customerMarkets.first &&
                                        value !== customerMarkets.second
                                    ).map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="question-block">
                        <h4>11. Over the next 1-3 years what new markets or regions are you planning to enter, or to further invest into?</h4>
                        <p className="question-subtitle">Select all markets that apply</p>

                        <div className={`checkbox-group ${hasAttemptedSubmit && futureMarkets.length === 0 ? 'validation-error' : ''}`}>
                            {[
                                ['northAmerica', 'North America'],
                                ['southAmerica', 'South America'],
                                ['ireland', 'Ireland'],
                                ['uk', 'UK'],
                                ['europe', 'Europe (ex Ireland)'],
                                ['asia', 'Asia'],
                                ['australia', 'Australia']
                            ].map(([value, label]) => (
                                <label key={value} className={`checkbox-label ${futureMarkets.includes('na') ? 'disabled' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={futureMarkets.includes(value)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setFutureMarkets(prev => [...prev, value]);
                                            } else {
                                                setFutureMarkets(prev => prev.filter(m => m !== value));
                                                if (value === 'asia') {
                                                    setAsianCountries({ first: '', second: '' });
                                                }
                                            }
                                        }}
                                        disabled={isSubmitted || futureMarkets.includes('na')}
                                    />
                                    {label}
                                </label>
                            ))}

                            {/* Add N/A option */}
                            <label className="checkbox-label na-option">
                                <input
                                    type="checkbox"
                                    checked={futureMarkets.includes('na')}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            // If N/A is selected, clear all other selections
                                            setFutureMarkets(['na']);
                                            // Clear Asian countries if they were set
                                            setAsianCountries({ first: '', second: '' });
                                        } else {
                                            // If N/A is unselected, just remove it
                                            setFutureMarkets([]);
                                        }
                                    }}
                                    disabled={isSubmitted}
                                />
                                N/A
                            </label>
                        </div>
                    </div>

                    {futureMarkets.includes('asia') && (
                        <div className="question-block">
                            <h4>12. What are the top two countries in Asia of most interest to your firm?</h4>
                            <p className="question-subtitle">Please enter the names of two Asian countries</p>

                            <div className="asian-countries-inputs" style={{ marginTop: '20px' }}>
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{
                                        display: 'inline-block',
                                        width: '140px',
                                        fontSize: '16px',
                                        color: '#333',
                                        fontWeight: 'normal'
                                    }}>First Country:</label>
                                    <input
                                        type="text"
                                        value={asianCountries.first}
                                        onChange={(e) => setAsianCountries(prev => {
                                            const newState = {
                                                ...prev,
                                                first: e.target.value
                                            };
                                            console.log('Updated Asian Countries:', newState);
                                            return newState;
                                        })}
                                        className={`${hasAttemptedSubmit ? (!asianCountries.first ? 'validation-error' : 'validation-success') : ''}`}
                                        placeholder="Enter country name"
                                        disabled={isSubmitted}
                                        style={{
                                            width: '250px',
                                            padding: '8px 12px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{
                                        display: 'inline-block',
                                        width: '140px',
                                        fontSize: '16px',
                                        color: '#333',
                                        fontWeight: 'normal'
                                    }}>Second Country:</label>
                                    <input
                                        type="text"
                                        value={asianCountries.second}
                                        onChange={(e) => setAsianCountries(prev => ({
                                            ...prev,
                                            second: e.target.value
                                        }))}
                                        className={`${hasAttemptedSubmit ? (!asianCountries.second ? 'validation-error' : 'validation-success') : ''}`}
                                        placeholder="Enter country name"
                                        disabled={isSubmitted}
                                        style={{
                                            width: '250px',
                                            padding: '8px 12px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom action buttons */}
            <div className="bottom-actions-container">
                {hasAnyData() && (
                    <div className="survey-actions-buttons bottom-actions">
                        {!isSubmitted ? (
                            <>
                                <button className="btn-save" onClick={handleSave}>
                                    <i className="fas fa-save"></i> Save Progress
                                </button>

                                <button className="btn-submit" onClick={async () => {
                                    await handleFinalSubmit();
                                    
                                }}>
                                    <i className="fas fa-paper-plane"></i> Submit Survey
                                </button>
                            </>
                        ) : (
                            <>
                                <button className="btn-view-results" onClick={() => setShowResults(true)}>
                                    <i className="fas fa-chart-bar"></i> View Results
                                </button>
                                <button
                                    className="btn-submit"
                                    onClick={handleDownloadResults}
                                    style={{ backgroundColor: 'var(--ifia-blue)' }}
                                >
                                    <i className="fas fa-download"></i> Download Results
                                </button>
                            </>
                        )}

                        <button className="btn-reset" onClick={handleReset}>
                            <i className="fas fa-undo"></i> Start Again
                        </button>

                        <button
                            className="btn-download-excel"
                            onClick={handleExportToExcel}
                            title="Download Excel template"
                        >
                            <i className="fas fa-file-excel"></i> Download Topics
                        </button>
                    </div>
                )}
            </div>

            {/* Add InfoModal for results preview */}
            <InfoModal
                show={showResults}
                onClose={() => {
                    console.log('Closing results modal');
                    setShowResults(false);
                }}
                title={isSubmitted ? "Survey Results" : "Survey Preview"}
                data={generateResultsHTML()}
                small={false}
                isSubmitted={isSubmitted}
            />

            {/* Add topic modal */}
            <ModalInnovation
                show={showAddTopicModal}
                onClose={() => setShowAddTopicModal(false)}
                data={{
                    title: "Add New Topic",
                    content: (
                        <div style={{ padding: "15px" }}>
                            <input
                                type="text"
                                placeholder="Enter topic name"
                                value={newTopic}
                                onChange={(e) => setNewTopic(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    marginBottom: "15px",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px"
                                }}
                            />
                            <div style={{ textAlign: "right" }}>
                                <button
                                    className="btn-add-topic"
                                    onClick={handleAddTopic}
                                    style={{
                                        padding: "8px 16px",
                                        backgroundColor: "var(--ifia-green)",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer"
                                    }}
                                >
                                    Add Topic
                                </button>
                            </div>
                        </div>
                    ),
                    hideDefaultContent: true
                }}
                small={true}
            />

            {/* Add Reset Confirmation Dialog */}
            {showResetDialog && (
                <div className="modal reset-dialog" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1000
                }}>
                    <div className="modal-content" style={{
                        backgroundColor: '#fff',
                        padding: '2rem',
                        borderRadius: '8px',
                        maxWidth: '500px',
                        width: '90%',
                        position: 'relative',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        textAlign: 'center'
                    }}>
                        <h2 style={{
                            color: '#385724',
                            marginBottom: '1.5rem',
                            fontSize: '1.5rem',
                            fontWeight: '600'
                        }}>Start Again</h2>
                        <div style={{ marginBottom: '2rem' }}>
                            <i className="fas fa-exclamation-circle" style={{
                                fontSize: '2.5rem',
                                color: '#e74c3c',
                                marginBottom: '1.5rem',
                                display: 'block'
                            }}></i>
                            <p style={{
                                fontSize: '1.1rem',
                                marginBottom: '1rem',
                                color: '#385724',
                                lineHeight: '1.5'
                            }}>
                                Are you sure you want to start again?
                            </p>
                            <p style={{
                                color: '#7f8c8d',
                                marginBottom: '1.5rem',
                                fontSize: '1rem',
                                lineHeight: '1.4'
                            }}>
                                This will remove all your current progress and restore the survey to its initial state.
                                This action cannot be undone.
                            </p>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                            <button
                                onClick={() => setShowResetDialog(false)}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: '#fff',
                                    color: '#385724',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmReset}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    border: 'none',
                                    borderRadius: '4px',
                                    backgroundColor: '#e74c3c',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500'
                                }}
                            >
                                Start Again
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Topic Modal */}
            <ModalInnovation
                show={showTopicModal}
                onClose={() => setShowTopicModal(false)}
                data={selectedTopicData || {}}
                small={true}
            />

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />



        </>
    );
}

export default DriversOfInnovationSurvey;